/**
 * AURIQ Take — the homepage / PDP customizer.
 *
 * A ring of pieces from a collection: one large at centre, the neighbours at
 * the stage edges, the rest parked outside; every step turns the whole ring
 * one slot. Configured door slides offer a motif, permitted choices, and one
 * private concept preview per explicit action. Other slides link to the piece.
 * Which pieces are doors is the app's call: the theme asks /api/take-config
 * once after first paint; the seven-handle map is only the offline default.
 *
 * Rules this file enforces:
 * - `api/generate` is called from the Take click handler only.
 * - New concepts allow a blank motif; edits require a specific instruction.
 * - Metal chips are a Recraft look. They never change variant_id or price
 *   and never print a karat.
 * - Concept checkout first saves its private context and links only an opaque
 *   help reference; no prompt, private asset URL, or review claim enters cart.
 * - The carousel (arrows, swipe, keys, auto-advance) never calls the API.
 *
 * Product pages use the spec sheet (data-layout="sheet"): no ring and no
 * auto-advance. has-still on the section switches the canvas from the
 * reference photo to the still. A failed take-config on a pending sheet
 * says Take is unavailable; a code-map door keeps working.
 */
(function (root) {
  "use strict";

  var USER_PROMPT_MIN = 10;
  var USER_PROMPT_MAX = 280;
  var NOT_SET_UP = "This piece is not set up for a take.";
  var CAPTION_REFERENCE = "Starting piece. Your piece appears here after the Take.";
  var CAPTION_AS_SHOWN = "As shown.";
  var UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  var METAOBJECT_GID_RE = /^gid:\/\/shopify\/Metaobject\/\d{1,20}$/;
  var PRODUCT_ID_RE = /^(?:gid:\/\/shopify\/Product\/)?[1-9]\d{0,19}$/;
  var FILE_GID_RE = /^gid:\/\/shopify\/(?:MediaImage|GenericFile)\/\d{1,20}$/;
  var SHOP_DOMAIN_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.myshopify\.com$/i;
  var PILOT_INTENT_STATES = ["SUPERVISED_INTENT_REVIEW_REQUIRED", "SUPERVISED_INTENT_REVIEW_STORAGE_PENDING",
    "SUPERVISED_INTENT_APPROVED", "SUPERVISED_INTENT_HELD", "SUPERVISED_INTENT_REVIEW_HELD", "SUPERVISED_REVIEW_UNRESOLVED"];
  var OWNER_INTENT_REVIEW_STATES = ["OWNER_INTENT_REVIEW_REQUIRED", "OWNER_INTENT_REVIEW_APPROVED", "OWNER_INTENT_REVIEW_HELD"];
  var OWNER_IMAGE_REVIEW_STATES = ["OWNER_IMAGE_REVIEW_REQUIRED", "OWNER_IMAGE_REVIEWED", "OWNER_IMAGE_HELD", "OWNER_IMAGE_UNRESOLVED"];
  PILOT_INTENT_STATES = PILOT_INTENT_STATES.concat(OWNER_INTENT_REVIEW_STATES);
  var PILOT_IMAGE_STATES = ["SUPERVISED_IMAGE_REVIEW_REQUIRED", "SUPERVISED_IMAGE_HELD", "SUPERVISED_IMAGE_REVIEWED", "SUPERVISED_IMAGE_UNRESOLVED"]
    .concat(OWNER_IMAGE_REVIEW_STATES);
  var PILOT_STATUS_ONLY_STATES = ["SUPERVISED_IMAGE_REVIEW_STORAGE_PENDING"];
  var LEGACY_PILOT_INTENT_STATES = ["OWNER_INTENT_REVIEW_REQUIRED", "OWNER_INTENT_REVIEW_APPROVED", "OWNER_INTENT_REVIEW_HELD"];
  var LEGACY_PILOT_IMAGE_STATES = ["OWNER_IMAGE_REVIEW_REQUIRED", "OWNER_IMAGE_REVIEWED", "OWNER_IMAGE_HELD", "OWNER_IMAGE_REVIEW_HELD", "OWNER_REVIEW_UNRESOLVED"];
  var PILOT_IMAGE_READ_STATES = PILOT_IMAGE_STATES.concat(LEGACY_PILOT_IMAGE_STATES);
  var LEGACY_PILOT_STATES = LEGACY_PILOT_INTENT_STATES.concat(LEGACY_PILOT_IMAGE_STATES);

  function savedDesignIdFromLocation() {
    try {
      var values = new root.URLSearchParams(root.location.search).getAll("auriq_design");
      return values.length === 1 && METAOBJECT_GID_RE.test(values[0]) ? values[0] : "";
    } catch (e) {
      return "";
    }
  }

  function helpProposalFromLocation() {
    try {
      var values = new root.URLSearchParams(root.location.search).getAll("auriq_help_proposal");
      if (!values.length) return { present: false, valid: true, reference: "" };
      return values.length === 1 && UUID_RE.test(values[0])
        ? { present: true, valid: true, reference: values[0].toLowerCase() }
        : { present: true, valid: false, reference: "" };
    } catch (e) {
      return { present: false, valid: false, reference: "" };
    }
  }

  function sameProductId(left, right) {
    var a = typeof left === "number" && Number.isSafeInteger(left) ? String(left) : left;
    var b = typeof right === "number" && Number.isSafeInteger(right) ? String(right) : right;
    return typeof a === "string" && typeof b === "string" && PRODUCT_ID_RE.test(a) && PRODUCT_ID_RE.test(b)
      && a.replace(/^gid:\/\/shopify\/Product\//, "") === b.replace(/^gid:\/\/shopify\/Product\//, "");
  }

  function hasLineage(traceId, attemptId) {
    return UUID_RE.test(traceId || "") && UUID_RE.test(attemptId || "");
  }

  var METALS = [
    { value: "yellow gold", label: "Yellow" },
    { value: "white gold", label: "White" },
    { value: "rose gold", label: "Rose" }
  ];

  var STONES = [
    { value: "clear diamond", label: "Diamond" },
    { value: "emerald", label: "Emerald" },
    { value: "sapphire", label: "Sapphire" },
    { value: "ruby", label: "Ruby" },
    { value: "moissanite", label: "Moissanite" },
    { value: "plain metal without stones", label: "Plain" }
  ];

  var CLOSURES = [
    { value: "screw back", label: "Screw" },
    { value: "latch back", label: "Latch" },
    { value: "spring", label: "Spring" },
    { value: "hinged", label: "Hinged" },
    { value: "fish hook", label: "Fish hook" },
    { value: "earwire", label: "Earwire" },
    { value: "push back", label: "Push" },
    { value: "french", label: "French" },
    { value: "omega", label: "Omega" },
    { value: "lever back", label: "Lever" }
  ];

  var CLASPS = [
    { value: "lobster claw", label: "Lobster" },
    { value: "spring ring", label: "Spring ring" },
    { value: "toggle", label: "Toggle" },
    { value: "box", label: "Box" },
    { value: "magnetic", label: "Magnetic" },
    { value: "hook and eye", label: "Hook & eye" }
  ];

  var PENDANT = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" }
  ];

  var BAILS = [
    { value: "fixed bail", label: "Fixed" },
    { value: "hinged bail", label: "Hinged" },
    { value: "hidden bail (slide bail)", label: "Hidden" },
    { value: "split ring bail or pinch bail", label: "Pinch" },
    { value: "station or integrated pendant", label: "Station" },
    { value: "slide", label: "Slide" }
  ];

  var WEAVES = [
    { value: "cable", label: "Cable" },
    { value: "curb", label: "Curb" },
    { value: "rolo", label: "Rolo" },
    { value: "snake", label: "Snake" },
    { value: "figaro", label: "Figaro" },
    { value: "rope", label: "Rope" },
    { value: "wheat", label: "Wheat" },
    { value: "herringbone", label: "Herringbone" },
    { value: "singapore", label: "Singapore" }
  ];

  var LENGTHS = [
    { value: "Collar (14-inch)", label: "Collar 14\"" },
    { value: "Choker (16-inch)", label: "Choker 16\"" },
    { value: "Princess (18-inch)", label: "Princess 18\"" },
    { value: "Matinee (22-inch)", label: "Matinee 22\"" },
    { value: "Opera (28-inch)", label: "Opera 28\"" },
    { value: "Rope (45-inch)", label: "Rope 45\"" }
  ];

  var SETTINGS = [
    { value: "bezel", label: "Bezel" },
    { value: "prong", label: "Prong" },
    { value: "flush", label: "Flush" },
    { value: "channel", label: "Channel" },
    { value: "pavé", label: "Pavé" },
    { value: "tension", label: "Tension" },
    { value: "none", label: "None" }
  ];

  var BANDS = [
    { value: "knife-edge", label: "Knife-edge" },
    { value: "comfort", label: "Comfort" },
    { value: "flat", label: "Flat" },
    { value: "cathedral", label: "Cathedral" },
    { value: "split", label: "Split" },
    { value: "bypass", label: "Bypass" },
    { value: "signet", label: "Signet" },
    { value: "open", label: "Open" }
  ];

  var PROFILES = [
    { value: "solitaire", label: "Solitaire" },
    { value: "cluster", label: "Cluster" },
    { value: "signet", label: "Signet" },
    { value: "sculptural", label: "Sculptural" }
  ];

  var CUFF_FORMS = [
    { value: "open cuff", label: "Open" },
    { value: "hinged", label: "Hinged" },
    { value: "slip-on", label: "Slip-on" },
    { value: "wrap", label: "Wrap" }
  ];

  var INNER_MARKS = [
    { value: "none", label: "None" },
    { value: "coordinates", label: "Coordinates" },
    { value: "date", label: "Date" },
    { value: "initials", label: "Initials" }
  ];

  var BAND_CONSTRUCTIONS = [
    { value: "plain", label: "Plain" },
    { value: "channel", label: "Channel" },
    { value: "pavé", label: "Pavé" },
    { value: "eternity", label: "Eternity" },
    { value: "half-eternity", label: "Half-eternity" }
  ];

  var EDGES = [
    { value: "knife-edge", label: "Knife-edge" },
    { value: "comfort", label: "Comfort" },
    { value: "flat", label: "Flat" }
  ];

  var GROUP_LABELS = {
    metal: "Visual metal colour",
    stones: "Concept stones",
    stoneA: "Left concept stone",
    stoneB: "Right concept stone",
    closure: "Closure",
    clasp: "Clasp",
    pendant: "Pendant",
    bail: "Bail",
    weave: "Weave",
    length: "Length",
    setting: "Setting",
    band: "Band",
    profile: "Profile",
    form: "Form",
    innerMark: "Inner mark",
    edge: "Edge"
  };

  // Mirror of DOOR_INTAKE in app/utils/promptCompiler.ts. Keep in step.
  var DOORS = {
    "flux-sculptural-ring": {
      formType: "ring",
      pair: false,
      motifPlaceholder: "a wave, a date, a city skyline…",
      options: {
        metal: "yellow gold",
        stones: "plain metal without stones",
        setting: "none",
        band: "bypass",
        profile: "sculptural"
      }
    },
    "orbit-ring": {
      formType: "ring",
      pair: false,
      motifPlaceholder: "five names, five months…",
      options: {
        metal: "yellow gold",
        stones: "sapphire",
        setting: "bezel",
        band: "comfort",
        profile: "cluster"
      }
    },
    "kinship-rings-duo": {
      formType: "ring",
      pair: true,
      motifPlaceholder: "who shares this pair…",
      options: {
        metal: "yellow gold",
        stoneA: "sapphire",
        stoneB: "emerald",
        setting: "bezel",
        band: "comfort",
        profile: "solitaire"
      }
    },
    "chronicle-bar-pendant": {
      formType: "necklace",
      pair: false,
      pendantLocked: true,
      motifPlaceholder: "the date, in digits…",
      options: {
        metal: "white gold",
        stones: "plain metal without stones",
        pendant: "yes",
        bail: "hidden bail (slide bail)",
        weave: "cable",
        clasp: "lobster claw",
        length: "Princess (18-inch)"
      }
    },
    "nocturne-pendant": {
      formType: "necklace",
      pair: false,
      pendantLocked: true,
      motifPlaceholder: "a moon phase, a night…",
      options: {
        metal: "white gold",
        stones: "plain metal without stones",
        pendant: "yes",
        bail: "fixed bail",
        weave: "cable",
        clasp: "lobster claw",
        length: "Princess (18-inch)"
      }
    },
    "entwine-pendant-pair": {
      formType: "necklace",
      pair: true,
      pendantLocked: true,
      motifPlaceholder: "two names, or two places…",
      options: {
        metal: "yellow gold",
        stones: "plain metal without stones",
        pendant: "yes",
        bail: "fixed bail",
        weave: "cable",
        clasp: "lobster claw",
        length: "Princess (18-inch)"
      }
    },
    "meridian-cuff": {
      formType: "cuff",
      pair: false,
      motifPlaceholder: "latitude, longitude, or a street…",
      options: {
        metal: "white gold",
        stones: "plain metal without stones",
        form: "open cuff",
        innerMark: "coordinates"
      }
    }
  };

  function copy(o) {
    var out = {};
    if (!o) return out;
    Object.keys(o).forEach(function (k) { out[k] = o[k]; });
    return out;
  }

  function inferForm(productType, handle) {
    var hay = (String(handle || "") + " " + String(productType || "")).toLowerCase();
    if (hay.indexOf("earring") !== -1) return "earring";
    if (hay.indexOf("neck") !== -1 || hay.indexOf("pendant") !== -1) return "necklace";
    if (hay.indexOf("cuff") !== -1 || hay.indexOf("bracelet") !== -1) return "cuff";
    if (hay.indexOf("band") !== -1) return "band";
    return "ring";
  }

  function clientFieldKeys(formType, pair) {
    var keys = ["metal"];
    if (pair && formType === "ring") keys.push("stoneA", "stoneB");
    else keys.push("stones");
    if (formType === "earring") keys.push("closure");
    else if (formType === "necklace") keys.push("clasp", "pendant", "bail", "weave", "length");
    else if (formType === "ring") keys.push("setting", "band", "profile");
    else if (formType === "cuff") keys.push("form", "innerMark");
    else if (formType === "band") keys.push("band", "edge", "innerMark");
    return keys;
  }

  function clientFieldsFor(intake) {
    var version = intake.clientFieldsVersion;
    var legacy = version === undefined;
    var fields = intake.clientFields;
    if (!legacy && version !== 2) return [];
    if (fields === undefined || (legacy && (fields === null || (Array.isArray(fields) && !fields.length)))) return null;
    if (!Array.isArray(fields)) return [];
    var allowed = clientFieldKeys(intake.formType, !!intake.pair);
    var seen = Object.create(null);
    for (var i = 0; i < fields.length; i++) {
      var key = fields[i];
      if (typeof key !== "string" || allowed.indexOf(key) === -1 || seen[key]) return [];
      seen[key] = true;
    }
    return fields.slice();
  }

  function doorFor(handle, productType, metafield) {
    if (metafield && typeof metafield === "object" && metafield.formType) {
      return {
        formType: metafield.formType,
        pair: !!metafield.pair,
        pendantLocked: !!metafield.pendantLocked,
        motifPlaceholder: metafield.motifPlaceholder || "a date, a name, a place…",
        options: copy(metafield.options),
        clientFieldsVersion: 2,
        clientFields: clientFieldsFor(metafield)
      };
    }
    var d = DOORS[handle];
    if (d) {
      return {
        formType: d.formType,
        pair: !!d.pair,
        pendantLocked: !!d.pendantLocked,
        motifPlaceholder: d.motifPlaceholder,
        options: copy(d.options),
        clientFields: null
      };
    }
    var formType = inferForm(productType, handle);
    var base = {
      formType: formType,
      pair: false,
      pendantLocked: false,
      motifPlaceholder: "a date, a name, a place…",
      options: { metal: "yellow gold", stones: "plain metal without stones" },
      clientFields: null
    };
    if (formType === "earring") base.options.closure = "lever back";
    else if (formType === "necklace") {
      base.options.pendant = "yes";
      base.options.bail = "fixed bail";
      base.options.weave = "cable";
      base.options.clasp = "lobster claw";
      base.options.length = "Princess (18-inch)";
    } else if (formType === "cuff") {
      base.options.form = "open cuff";
      base.options.innerMark = "none";
    } else if (formType === "band") {
      base.options.band = "plain";
      base.options.edge = "comfort";
      base.options.innerMark = "none";
    } else {
      base.options.setting = "none";
      base.options.band = "comfort";
      base.options.profile = "solitaire";
    }
    return base;
  }

  function groupsFor(door, options) {
    var groups = [{ key: "metal", items: METALS }];
    if (door.pair && door.formType === "ring") {
      groups.push({ key: "stoneA", items: STONES });
      groups.push({ key: "stoneB", items: STONES });
    } else {
      groups.push({ key: "stones", items: STONES });
    }
    if (door.formType === "earring") {
      groups.push({ key: "closure", items: CLOSURES });
    } else if (door.formType === "necklace") {
      groups.push({ key: "clasp", items: CLASPS });
      var pendantItems = door.pendantLocked
        ? PENDANT.filter(function (p) { return p.value === "yes"; })
        : PENDANT;
      groups.push({ key: "pendant", items: pendantItems });
      if ((options && options.pendant) === "yes") {
        groups.push({ key: "bail", items: BAILS });
      }
      groups.push({ key: "weave", items: WEAVES });
      groups.push({ key: "length", items: LENGTHS });
    } else if (door.formType === "ring") {
      groups.push({ key: "setting", items: SETTINGS });
      groups.push({ key: "band", items: BANDS });
      groups.push({ key: "profile", items: PROFILES });
    } else if (door.formType === "cuff") {
      groups.push({ key: "form", items: CUFF_FORMS });
      groups.push({ key: "innerMark", items: INNER_MARKS });
    } else if (door.formType === "band") {
      groups.push({ key: "band", items: BAND_CONSTRUCTIONS });
      groups.push({ key: "edge", items: EDGES });
      groups.push({ key: "innerMark", items: INNER_MARKS });
    }
    groups.forEach(function (g) { g.label = GROUP_LABELS[g.key] || g.key; });
    return groups;
  }

  function optionsWithDraft(intake, choices) {
    var options = copy(intake.options);
    var allowed = intake.clientFields;
    groupsFor(intake, { pendant: "yes" }).forEach(function (group) {
      if (allowed && allowed.indexOf(group.key) === -1) return;
      var value = choices[group.key];
      if (group.items.some(function (item) { return item.value === value; })) options[group.key] = value;
    });
    if (intake.pendantLocked) options.pendant = "yes";
    if (intake.formType === "necklace" && options.pendant === "no") delete options.bail;
    return options;
  }

  // Tab-scoped recovery keeps the motif and explicit look choices, never a still or price.
  var TAKE_DRAFT_KEY = "auriq.take-draft.v1";

  function readTakeDraft(handle, owner) {
    try {
      var raw = root.sessionStorage.getItem(TAKE_DRAFT_KEY);
      if (!raw) return null;
      var saved = JSON.parse(raw);
      if (!saved || typeof saved !== "object" || !saved.drafts || typeof saved.drafts !== "object" || Array.isArray(saved.drafts)) return null;
      if (saved.owner !== owner && saved.owner !== "guest") return null;
      // Claim the whole tab before exposing any product's draft after sign-in.
      if (saved.owner !== owner) {
        saved.owner = owner;
        try { root.sessionStorage.setItem(TAKE_DRAFT_KEY, JSON.stringify(saved)); }
        catch (e) {
          root.sessionStorage.removeItem(TAKE_DRAFT_KEY);
          return null;
        }
      }
      var row = saved.drafts[handle];
      if (!row || typeof row !== "object" || Array.isArray(row)) return null;
      if (row.motif != null && (typeof row.motif !== "string" || row.motif.length > USER_PROMPT_MAX)) return null;
      if (row.options != null && !plainStringOptions(row.options)) return null;
      return row;
    } catch (e) {
      return null;
    }
  }

  function writeTakeDraft(handle, owner, motif, options) {
    try {
      var saved = { owner: owner, drafts: {} };
      var raw = root.sessionStorage.getItem(TAKE_DRAFT_KEY);
      if (raw) {
        var parsed;
        try { parsed = JSON.parse(raw); } catch (e) { /* a new explicit edit replaces an unreadable draft */ }
        if (parsed && parsed.owner === owner && parsed.drafts && typeof parsed.drafts === "object" && !Array.isArray(parsed.drafts)) saved = parsed;
      }
      var slim = {};
      if (options && plainStringOptions(options)) {
        Object.keys(options).forEach(function (key) { slim[key] = options[key]; });
      }
      saved.drafts[handle] = { motif: String(motif || "").slice(0, USER_PROMPT_MAX), options: slim };
      root.sessionStorage.setItem(TAKE_DRAFT_KEY, JSON.stringify(saved));
    } catch (e) { /* keep the current page usable when browser storage is unavailable */ }
  }

  function motifValid(raw) {
    var text = String(raw == null ? "" : raw).trim();
    if (text.length < USER_PROMPT_MIN || text.length > USER_PROMPT_MAX) return false;
    var seen = {};
    var distinct = 0;
    for (var i = 0; i < text.length; i++) {
      var ch = text.charAt(i);
      if (/\s/.test(ch)) continue;
      if (!seen[ch]) { seen[ch] = true; distinct++; }
      if (distinct > 1) return true;
    }
    return false;
  }

  function readVisitor() {
    try {
      var testRaw = root.sessionStorage.getItem("auriq.test-session.v1");
      if (testRaw !== null) {
        var test = JSON.parse(testRaw);
        var shopDomain = root.AuriqConfig && root.AuriqConfig.shopDomain;
        var shopMatches = !!test && (typeof shopDomain !== "string" || test.shop === shopDomain);
        return test && SHOP_DOMAIN_RE.test(test.shop || "") && shopMatches
          && test.origin === root.location.origin
          && UUID_RE.test(test.id || "") && /^[A-Za-z0-9_-]{22}$/.test(test.hmac || "")
          && UUID_RE.test(test.sessionId || "") ? { id: test.id, hmac: test.hmac } : null;
      }
      var id = root.localStorage.getItem("auriq_vid");
      var hmac = root.localStorage.getItem("auriq_vhmac");
      return id && hmac ? { id: id, hmac: hmac } : null;
    } catch (e) {
      return null;
    }
  }

  function readVisitorId() {
    try {
      var testRaw = root.sessionStorage.getItem("auriq.test-session.v1");
      if (testRaw !== null) {
        var test = JSON.parse(testRaw);
        var shopDomain = root.AuriqConfig && root.AuriqConfig.shopDomain;
        var shopMatches = !!test && (typeof shopDomain !== "string" || test.shop === shopDomain);
        return test && SHOP_DOMAIN_RE.test(test.shop || "") && shopMatches && test.origin === root.location.origin &&
          UUID_RE.test(test.id || "") ? test.id : "";
      }
      var id = root.localStorage.getItem("auriq_vid");
      return id && /^[A-Za-z0-9_-]{1,80}$/.test(id) ? id : "";
    } catch (e) {
      return "";
    }
  }

  function ensureVisitorForExplicitAction() {
    var api = root.Auriq;
    if (!api || typeof api.ensureGuestIdentity !== "function") return Promise.resolve(readVisitor());
    try {
      return Promise.resolve(api.ensureGuestIdentity()).then(function (visitor) {
        return visitor && visitor.id && visitor.hmac ? visitor : null;
      }, function () { return null; });
    } catch (e) { return Promise.resolve(null); }
  }

  // Event names and props match the dossier's strict per-type schemas
  // (services/dossier/src/events/validate.ts); anything else is rejected.
  function track(type, props) {
    try {
      if (root.Auriq && typeof root.Auriq.track === "function") root.Auriq.track(type, props);
    } catch (e) { /* tracker is optional */ }
  }

  function productCtx(door) {
    var ctx = {};
    if (door.id) ctx.product_id = String(door.id);
    var variant = variantForSlide(door, door.variantId);
    if (variant && variant.available === true && door.variantAvailable === true) ctx.variant_id = String(variant.id);
    if (door.title) ctx.product_title = String(door.title).slice(0, 256);
    return ctx;
  }

  function variantForSlide(slide, variantId) {
    if (!slide || !/^[1-9]\d{0,19}$/.test(String(variantId || "")) || !Array.isArray(slide.variants)) return null;
    var id = String(variantId);
    for (var i = 0; i < slide.variants.length; i++) {
      var variant = slide.variants[i];
      if (variant && String(variant.id) === id) return variant;
    }
    return null;
  }

  function purchasableVariant(slide) {
    var variant = variantForSlide(slide, slide && slide.variantId);
    return variant && variant.available === true && slide.variantAvailable === true ? variant : null;
  }

  function readCartSnapshot() {
    return root.fetch("/cart.js", { method: "GET", credentials: "same-origin", cache: "no-store" }).then(function (response) {
      if (!response.ok) throw new Error("The current bag could not be checked.");
      return response.json();
    }).then(function (cart) {
      if (!cart || !Array.isArray(cart.items) || !Number.isSafeInteger(cart.item_count)) {
        throw new Error("The current bag could not be checked.");
      }
      return cart;
    });
  }

  function cartQuantity(cart, variantId, propertyName, propertyValue) {
    var quantity = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (!item || String(item.variant_id || "") !== variantId) continue;
      if (propertyName && (!item.properties || item.properties[propertyName] !== propertyValue)) continue;
      if (Number.isSafeInteger(item.quantity) && item.quantity > 0) quantity += item.quantity;
    }
    return quantity;
  }

  function withCtx(door, extra) {
    var out = productCtx(door);
    Object.keys(extra || {}).forEach(function (k) {
      if (extra[k] !== undefined) out[k] = extra[k];
    });
    return out;
  }

  function readConfig(el) {
    var node = el.querySelector("[data-auriq-take-config]");
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (e) { return null; }
  }

  var DOOR_ORDER = Object.keys(DOORS);

  function isDoorHandle(handle) {
    return DOOR_ORDER.indexOf(handle) !== -1;
  }

  function pad2(n) {
    return (n < 10 ? "0" : "") + n;
  }

  function reducedMotion() {
    try {
      return !!(root.matchMedia && root.matchMedia("(prefers-reduced-motion: reduce)").matches);
    } catch (e) {
      return false;
    }
  }

  // Ring move 900 ms, snap 500 ms, panel words swap 200 ms in (see auriq-take.css).
  var SNAP_MS = 500;
  var PANEL_SWAP_AT_MS = 200;
  var DRAG_UNIT = 0.58;      // one slot of travel, as a fraction of the stage width
  var SNAP_FRACTION = 0.12;  // drag past this fraction of S commits the step
  var SNAP_VELOCITY = 0.5;   // px per ms
  var MOBILE_MAX = 989;

  function lerp(a, b, t) { return a + (b - a) * t; }

  // Pure geometry of the ring. `slot` is the signed ring distance from the
  // centre (0 = the current piece, ±1 the neighbours), S the stage width.
  // Two-dimensional only: translate, scale, opacity.
  function ringPose(slot, S, isMobile) {
    var sign = slot < 0 ? -1 : 1;
    var a = Math.min(Math.abs(slot), 2.5);
    var x, scale, opacity, y;
    if (a <= 1) {
      x = lerp(0, 0.58 * S, a);
      scale = lerp(1, 0.66, a);
      opacity = lerp(1, 0.5, a);
      y = lerp(0, 0.03 * S, a);
    } else {
      var t = Math.min(a - 1, 1);
      x = lerp(0.58 * S, 1.02 * S, a - 1);
      scale = lerp(0.66, 0.5, t);
      opacity = lerp(0.5, 0, t);
      y = 0.03 * S;
    }
    return {
      x: sign * x,
      y: y,
      scale: scale,
      opacity: Math.max(0, opacity),
      z: 10 - Math.round(a),
      interactive: a < 2,
      centreWidth: (isMobile ? 0.84 : 0.72) * S
    };
  }

  function mod(n, m) { return ((n % m) + m) % m; }

  // Shortest signed distance around the ring from `index` to `i`.
  function ringSlot(i, index, n) {
    return mod(i - index + n / 2, n) - n / 2;
  }

  // ---------------------------------------------------------------------------
  // Carousel: a ring of <figure data-slide> inside [data-stage]. One piece
  // large at centre, the neighbours at the edges, the rest parked outside.
  // Every step moves every piece one slot along the same path. It only moves
  // slides; it never touches the network.
  // ---------------------------------------------------------------------------
  function Carousel(take) {
    this.take = take;
    var el = take.el;
    this.el = el;
    this.stage = el.querySelector("[data-stage]");
    this.track = el.querySelector("[data-track]");
    this.figures = Array.prototype.slice.call(el.querySelectorAll("[data-slide]"));
    this.prev = el.querySelector("[data-prev]");
    this.next = el.querySelector("[data-next]");
    this.counter = el.querySelector("[data-counter]");
    this.index = 0;
    this.slots = this.figures.map(function () { return 0; });
    this.offset = 0;
    this.S = 1;
    this.isMobile = false;
    this.timer = null;
    this.interacted = false;
    this.reduced = reducedMotion();
    this.drag = null;
    this.frame = null;
    this.suppressClick = false;
    if (this.reduced) el.classList.add("is-reduced");
    this.measure();
    this.bind();
  }

  Carousel.prototype.count = function () {
    return this.figures.length;
  };

  // Cache the stage width; no layout reads happen inside pointer handlers.
  Carousel.prototype.measure = function (width) {
    var w = typeof width === "number" ? width : 0;
    if (!w && this.stage) w = this.stage.clientWidth || 0;
    this.S = Math.max(1, w);
    var vw = root.innerWidth || 0;
    this.isMobile = vw > 0 && vw <= MOBILE_MAX;
    if (this.figures.length) this.applyPoses();
  };

  Carousel.prototype.bind = function () {
    var self = this;
    var el = this.el;

    if (this.prev) {
      this.prev.addEventListener("click", function () { self.step(-1, true); });
    }
    if (this.next) {
      this.next.addEventListener("click", function () { self.step(1, true); });
    }

    if (this.stage) {
      this.stage.addEventListener("pointerdown", function (e) { self.onPointerDown(e); });
      this.stage.addEventListener("pointermove", function (e) { self.onPointerMove(e); });
      this.stage.addEventListener("pointerup", function (e) { self.onPointerUp(e); });
      this.stage.addEventListener("pointercancel", function () { self.endDrag(false); });

      // A tap or click on a neighbour steps to it.
      this.stage.addEventListener("click", function (e) {
        if (self.suppressClick) { self.suppressClick = false; e.preventDefault(); return; }
        var fig = e.target && e.target.closest ? e.target.closest("[data-slide]") : null;
        if (!fig) return;
        var i = self.figures.indexOf(fig);
        if (i === -1 || i === self.index) return;
        var slot = ringSlot(i, self.index, self.count());
        if (Math.abs(slot) < 2) self.step(slot < 0 ? -1 : 1, true);
      });

      this.stage.addEventListener("keydown", function (e) {
        var fig = e.target && e.target.closest ? e.target.closest("[data-slide]") : null;
        if (!fig || (e.key !== "Enter" && e.key !== " ")) return;
        e.preventDefault();
        fig.click();
      });
    }

    // Arrow keys while focus is inside the section, except in text fields.
    el.addEventListener("keydown", function (e) {
      if (self.take && self.take.sheet) return;
      var tag = e.target && e.target.tagName;
      if (tag === "TEXTAREA" || tag === "INPUT" || tag === "SELECT") return;
      if (e.key === "ArrowRight") { e.preventDefault(); self.step(1, true); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); self.step(-1, true); }
    });

    // First interaction anywhere inside the section ends auto-advance.
    ["pointerdown", "touchstart", "keydown", "focusin"].forEach(function (type) {
      el.addEventListener(type, function () { self.stopAuto(); }, { passive: true });
    });

    if (this.stage && typeof root.ResizeObserver === "function") {
      var ro = new root.ResizeObserver(function () { self.measure(); });
      ro.observe(this.stage);
    } else {
      root.addEventListener("resize", function () { self.measure(); });
    }
  };

  Carousel.prototype.step = function (dir, byUser) {
    var n = this.count();
    if (n < 2) return;
    if (byUser) this.stopAuto();
    this.goTo(mod(this.index + dir, n), false);
  };

  // Pose every figure for the current index plus the live drag offset.
  Carousel.prototype.applyPoses = function () {
    var n = this.count();
    if (n < 2 || (this.take && this.take.sheet)) {
      // One piece, or the spec sheet: full width, no ring pose.
      for (var k = 0; k < n; k++) {
        var f = this.figures[k];
        f.style.transform = "";
        f.style.opacity = "";
        f.style.zIndex = "";
        f.style.pointerEvents = "";
        f.setAttribute("data-slot", "0");
      }
      return;
    }
    for (var i = 0; i < n; i++) {
      var fig = this.figures[i];
      var slot = this.slots[i] + this.offset;
      var p = ringPose(slot, this.S, this.isMobile);
      fig.style.transform = "translate3d(" + p.x.toFixed(2) + "px, " + p.y.toFixed(2) + "px, 0) scale(" + p.scale.toFixed(4) + ")";
      fig.style.opacity = String(p.opacity.toFixed(3));
      fig.style.zIndex = String(p.z);
      fig.style.pointerEvents = p.interactive ? "" : "none";
      fig.setAttribute("data-slot", String(Math.round(this.slots[i])));
    }
  };

  Carousel.prototype.goTo = function (index, instant) {
    var n = this.count();
    if (!n) return;
    index = mod(index, n);
    var previous = this.index;
    this.index = index;
    var jumped = [];
    for (var i = 0; i < n; i++) {
      var was = this.slots[i];
      var now = ringSlot(i, index, n);
      this.slots[i] = now;
      var fig = this.figures[i];
      var on = i === index;
      var neighbour = Math.abs(now) === 1 || (Math.abs(now) < 2 && !on);
      fig.classList.toggle("is-current", on);
      fig.setAttribute("aria-hidden", on ? "false" : "true");
      if (neighbour) {
        fig.setAttribute("role", "button");
        fig.setAttribute("tabindex", "0");
        fig.setAttribute("aria-label", fig.getAttribute("data-slide-title") || "");
      } else {
        fig.removeAttribute("role");
        fig.removeAttribute("tabindex");
        fig.removeAttribute("aria-label");
      }
      // The wrap: a figure that crosses the ring is invisible at that distance,
      // so it may jump without a transition.
      if (instant || this.reduced || Math.abs(now - was) > 2) {
        fig.classList.add("is-jump");
        jumped.push(fig);
      } else {
        fig.classList.remove("is-jump");
      }
      // Load the centre and its neighbours eagerly once they come near.
      if (Math.abs(now) <= 1) {
        var img = fig.querySelector("[data-photo]");
        if (img && img.getAttribute("loading") === "lazy") img.removeAttribute("loading");
      }
    }
    this.offset = 0;
    this.applyPoses();
    if (jumped.length) {
      var clear = function () {
        for (var j = 0; j < jumped.length; j++) jumped[j].classList.remove("is-jump");
      };
      if (typeof root.requestAnimationFrame === "function") root.requestAnimationFrame(clear);
      else root.setTimeout(clear, 16);
    }
    if (this.counter) this.counter.textContent = pad2(index + 1) + " / " + pad2(n);
    this.take.onSlide(index, instant || this.reduced || previous === index);
  };

  // --- drag-follow -----------------------------------------------------------

  Carousel.prototype.onPointerDown = function (e) {
    if (this.count() < 2 || this.reduced || (this.take && this.take.sheet)) return;
    if (e.target && e.target.closest && e.target.closest("button, a")) return;
    if (typeof e.button === "number" && e.button !== 0) return;
    this.drag = { x0: e.clientX, y0: e.clientY, t0: Date.now(), dx: 0, active: false, id: e.pointerId };
  };

  Carousel.prototype.onPointerMove = function (e) {
    var d = this.drag;
    if (!d) return;
    var dx = e.clientX - d.x0;
    var dy = e.clientY - d.y0;
    if (!d.active) {
      // Mostly vertical: let the page scroll.
      if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) { this.drag = null; return; }
      if (Math.abs(dx) < 4) return;
      d.active = true;
      this.stopAuto();
      this.el.classList.add("is-dragging");
      if (this.stage && this.stage.setPointerCapture && typeof d.id === "number") {
        try { this.stage.setPointerCapture(d.id); } catch (err) { /* not capturable */ }
      }
    }
    d.dx = dx;
    var self = this;
    if (this.frame === null) {
      var run = function () {
        self.frame = null;
        if (!self.drag) return;
        self.offset = self.drag.dx / (DRAG_UNIT * self.S);
        self.applyPoses();
      };
      if (typeof root.requestAnimationFrame === "function") this.frame = root.requestAnimationFrame(run);
      else { this.frame = 1; root.setTimeout(run, 16); }
    }
  };

  Carousel.prototype.onPointerUp = function (e) {
    var d = this.drag;
    if (!d) return;
    if (!d.active) { this.drag = null; return; }
    var dx = e.clientX - d.x0;
    // A flick counts when it is both quick and not tiny; dt is floored so a
    // synthetic or jittery gesture cannot read as infinitely fast.
    var dt = Math.max(150, Date.now() - d.t0);
    var flick = Math.abs(dx) > 0.05 * this.S && Math.abs(dx) / dt > SNAP_VELOCITY;
    var commit = Math.abs(dx) > SNAP_FRACTION * this.S || flick;
    this.suppressClick = true;
    this.endDrag(commit, dx);
  };

  Carousel.prototype.endDrag = function (commit, dx) {
    var self = this;
    var d = this.drag;
    this.drag = null;
    if (this.frame !== null) {
      if (typeof root.cancelAnimationFrame === "function" && this.frame !== 1) root.cancelAnimationFrame(this.frame);
      this.frame = null;
    }
    if (!d || !d.active) return;
    this.el.classList.remove("is-dragging");
    this.el.classList.add("is-snapping");
    root.setTimeout(function () { self.el.classList.remove("is-snapping"); }, SNAP_MS);
    if (commit && typeof dx === "number" && dx !== 0) {
      // Dragging to the right pulls the previous piece into the centre.
      this.step(dx > 0 ? -1 : 1, true);
    } else {
      this.offset = 0;
      this.applyPoses();
    }
  };

  // --- auto-advance ------------------------------------------------------------

  Carousel.prototype.startAuto = function (seconds) {
    var self = this;
    if (this.take && this.take.sheet) return;
    if (this.timer || this.interacted || this.reduced || this.count() < 2) return;
    var ms = Math.max(50, Number(seconds || 7) * 1000);
    this.timer = root.setInterval(function () {
      if (self.take.anyStill()) { self.stopAuto(); return; }
      self.goTo(mod(self.index + 1, self.count()), false);
    }, ms);
  };

  Carousel.prototype.stopAuto = function () {
    this.interacted = true;
    if (this.timer) {
      root.clearInterval(this.timer);
      this.timer = null;
    }
  };

  // ---------------------------------------------------------------------------
  // Take: the panel over the current slide. Door slides take; look slides link.
  // ---------------------------------------------------------------------------
  function Take(el) {
    this.el = el;
    this.cfg = readConfig(el) || {};
    this.draftOwner = this.cfg.customerId ? "customer:" + String(this.cfg.customerId) : "guest";
    this.savedDesignId = savedDesignIdFromLocation();
    this.helpProposalQuery = helpProposalFromLocation();
    this.helpProposalStatus = this.helpProposalQuery.present
      ? this.helpProposalQuery.valid ? "PENDING" : "INVALID"
      : "NONE";
    this.helpProposalResolveStarted = false;
    this.helpProposalAcceptEventId = "";
    this.helpProposal = null;
    this.savedDesignResumeStarted = false;
    this.savedDesignResumeBusy = false;
    this.savedDesignResumeWaitingForRecovery = false;
    this.savedDesignHistory = null;
    this.savedDesignRetryButton = null;
    this.slides = (this.cfg.slides || []).map(function (s) {
      if (s.kind === "door") s.intake = doorFor(s.handle, s.type, s.intake);
      s.id = String(s.id || "");
      var initialVariant = variantForSlide(s, s.variantId);
      s.variantId = initialVariant ? String(initialVariant.id) : "";
      s.variantAvailable = !!(initialVariant && initialVariant.available === true);
      if (initialVariant && typeof initialVariant.price === "string") s.price = initialVariant.price;
      return s;
    });
    if (!this.slides.length) return;

    this.q = function (sel) { return el.querySelector(sel); };
    this.alternate = this.q("[data-alternate]");
    this.alternateImg = this.q("[data-alternate-img]");
    this.eyebrow = this.q("[data-eyebrow]");
    this.title = this.q("[data-title]");
    this.price = this.q("[data-price]");
    this.doorPanel = this.q("[data-door-panel]");
    this.lookPanel = this.q("[data-look-panel]");
    this.view = this.q("[data-view]");
    this.fields = this.q("[data-fields]");
    this.motif = this.q("[data-motif]");
    this.count = this.q("[data-count]");
    this.take = this.q("[data-take]");
    this.quota = this.q("[data-quota]");
    this.status = this.q("[data-status]");
    if (this.savedDesignId && this.status && this.status.parentNode) {
      this.savedDesignRetryButton = document.createElement("button");
      this.savedDesignRetryButton.type = "button";
      this.savedDesignRetryButton.className = "auriq-take__save";
      this.savedDesignRetryButton.textContent = "Try restoring saved design again";
      this.savedDesignRetryButton.hidden = true;
      this.savedDesignRetryButton.setAttribute("data-saved-design-retry", "");
      this.status.parentNode.insertBefore(this.savedDesignRetryButton, this.status.nextSibling);
    }
    this.recoveryPanel = this.q("[data-generation-recovery]");
    this.recoveryButton = this.q("[data-generation-recovery-button]");
    this.resumeButton = this.q("[data-generation-resume-button]");
    this.finishImagesButton = this.q("[data-generation-finish-button]");
    this.recoveryStatus = this.q("[data-generation-recovery-status]");
    this.designHelp = this.q("[data-design-help]");
    this.designProposal = this.q("[data-design-proposal]");
    this.designProposalStatus = this.q("[data-proposal-status]");
    this.designProposalClarification = this.q("[data-proposal-clarification]");
    this.designProposalOriginalText = this.q("[data-proposal-original-text]");
    this.designProposalOriginalOptions = this.q("[data-proposal-original-options]");
    this.designProposalText = this.q("[data-proposal-text]");
    this.designProposalOptions = this.q("[data-proposal-options]");
    this.designProposalAccept = this.q("[data-proposal-accept]");
    this.designProposalDismiss = this.q("[data-proposal-dismiss]");
    this.after = this.q("[data-after]");
    this.bag = this.q("[data-add-to-bag]");
    this.save = this.q("[data-save]");
    this.asShown = this.q("[data-as-shown]");
    this.asShownButton = this.q("[data-add-as-shown]");
    this.asShownLink = this.q("[data-add-as-shown-link]");
    this.sheet = el.getAttribute("data-layout") === "sheet";
    this.canvas = this.q("[data-canvas]");
    this.caption = this.q("[data-canvas-caption]");
    this.corner = this.q("[data-canvas-reference]");
    this.unavailable = this.q("[data-unavailable]");
    this.priceNote = this.q("[data-price-note]");
    this.about = this.q("[data-about]");
    this.designSummary = this.q("[data-design-summary]");
    this.gallery = this.q("[data-design-gallery]");
    this.references = el.querySelectorAll("[data-reference]");

    this.busy = false;
    this.recoveryBusy = false;
    this.remaining = null;
    this.available = null;
    this.pending = 0;
    this.trafficThrottled = false;
    this.cap = null;
    this.resetsAt = null;

    // Per-door state, keyed by handle, so moving away and back keeps her still.
    this.state = {};
    this.door = null;

    var start = 0;
    for (var i = 0; i < this.slides.length; i++) {
      if (this.slides[i].handle === this.cfg.defaultHandle) { start = i; break; }
    }

    this.swapTimer = null;
    this.quotaRequested = false;
    this.configRequested = false;
    this.bind();
    this.updateDesignHelpProposalView();
    this.carousel = new Carousel(this);
    this.carousel.goTo(start, true);
    if (this.designHelp && root.AuriqDesignHelp) {
      var helpSelf = this;
      this.designHelpApi = root.AuriqDesignHelp.mount(this.designHelp, {
        appUrl: this.cfg.appUrl,
        customerId: this.cfg.customerId || "",
        getVisitor: ensureVisitorForExplicitAction,
        onSaved: function (saved, capturedContext) {
          var productId = capturedContext && capturedContext.productId;
          var traceId = saved && saved.trace_id;
          if (typeof productId !== "string" || !UUID_RE.test(traceId || "")) return;
          for (var i = 0; i < helpSelf.slides.length; i++) {
            var slide = helpSelf.slides[i];
            if (String(slide.id) !== productId) continue;
            helpSelf.stateFor(slide).helpTraceId = traceId;
            return;
          }
        },
        getContext: function () {
          var door = helpSelf.door || helpSelf.slides[helpSelf.carousel ? helpSelf.carousel.index : 0];
          if (!door) return {};
          var state = helpSelf.stateFor(door);
          var historical = helpSelf.savedDesignHistory && helpSelf.savedDesignHistory.designInput;
          var selectedInput = state.selected && state.selected.designInput;
          var purchaseVariant = purchasableVariant(door);
          return {
            productId: String(door.id || ""),
            productTitle: door.title || "",
            purchaseVariantId: purchaseVariant ? String(purchaseVariant.id) : "",
            formType: selectedInput ? selectedInput.formType : historical ? historical.formType : door.intake && door.intake.formType,
            pair: selectedInput ? selectedInput.pair : historical ? historical.pair : !!(door.intake && door.intake.pair),
            options: copy(selectedInput ? selectedInput.options : state.options),
            userPrompt: selectedInput ? selectedInput.userPrompt : helpSelf.motif ? String(helpSelf.motif.value || "") : String(state.motif || ""),
            traceId: state.selected && state.selected.trace_id || state.lastFailureTraceId || state.traceId || state.helpTraceId,
            selected: state.selected,
            handoffReason: state.handoffReason || (helpSelf.available !== null && helpSelf.available <= 0 ? "QUOTA" : "VOLUNTARY"),
            latestInput: door.intake && door.intake.formType ? {
              productId: String(door.id || ""),
              formType: door.intake.formType,
              pair: !!door.intake.pair,
              options: copy(state.options),
              userPrompt: helpSelf.motif ? String(helpSelf.motif.value || "") : String(state.motif || "")
            } : undefined
          };
        }
      });
    }
    if (this.cfg.autoAdvance && !this.sheet) this.carousel.startAuto(this.cfg.autoAdvanceSeconds);
    this.refreshQuota();
    // After first paint, ask the app once which pieces carry Take.
    var self = this;
    var later = typeof root.requestAnimationFrame === "function"
      ? function (fn) { root.requestAnimationFrame(function () { root.setTimeout(fn, 0); }); }
      : function (fn) { root.setTimeout(fn, 0); };
    later(function () { self.loadConfig(); });
  }

  // GET {appUrl}/api/take-config?handles=a,b,c → { items: { [handle]: { configured, intake } } }.
  // configured → door with the server intake; not configured or missing → look.
  // No answer → code-map doors stay doors. On the ring, pending becomes look.
  // On the spec sheet, pending says Take is unavailable instead of a silent downgrade.
  Take.prototype.loadConfig = function () {
    var self = this;
    if (this.configRequested) return Promise.resolve();
    this.configRequested = true;
    var handles = this.slides.map(function (s) { return s.handle; }).slice(0, 50);
    if (!this.cfg.appUrl || !handles.length) {
      this.applyConfig(null);
      return Promise.resolve();
    }
    var url = this.cfg.appUrl + "/api/take-config?handles=" + encodeURIComponent(handles.join(","));
    return root.fetch(url, { method: "GET", headers: { Accept: "application/json" } })
      .then(function (r) {
        if (!r.ok) throw new Error("take-config " + r.status);
        return r.json();
      })
      .then(function (data) {
        if (!data || typeof data !== "object" || !data.items || typeof data.items !== "object") {
          throw new Error("take-config malformed");
        }
        if (Number.isSafeInteger(data.trial_limit) && data.trial_limit > 0) {
          self.setQuota(self.remaining === null ? undefined : self.remaining, data.trial_limit,
            self.resetsAt, self.available === null ? undefined : self.available, self.pending, self.trafficThrottled);
        }
        self.applyConfig(data.items);
      })
      .catch(function (err) {
        try { root.console.warn("AURIQ take-config unavailable; using code defaults.", err && err.message); } catch (e) { /* no console */ }
        self.applyConfig(null);
      });
  };

  Take.prototype.applyConfig = function (items) {
    var self = this;
    var figs = this.carousel ? this.carousel.figures : [];
    this.slides.forEach(function (slide, i) {
      var answer = items ? items[slide.handle] : undefined;
      var kind;
      var intake = null;
      if (items) {
        if (answer && answer.configured) {
          kind = "door";
          intake = answer.intake && typeof answer.intake === "object" && answer.intake.formType ? answer.intake : null;
        } else {
          kind = "look";
        }
      } else if (slide.kind === "door") {
        kind = "door";
      } else if (self.sheet) {
        kind = "unavailable";
      } else {
        kind = "look";
      }
      if (kind === "door") {
        var resolved = doorFor(slide.handle, slide.type, intake || slide.intake);
        var st = self.state[slide.handle];
        slide.intake = resolved;
        // Keep explicit permitted choices when the delayed defaults arrive.
        // A still retains its historical input, including incompatible choices.
        if (st && !st.stills.length) {
          st.options = optionsWithDraft(resolved, st.draftOptions);
        }
      } else {
        slide.intake = null;
      }
      slide.kind = kind;
      if (figs[i]) figs[i].setAttribute("data-kind", kind);
    });
    this.configResolved = true;
    if (this.door) this.paintPanel(this.door, null);
    this.refreshQuota();
    this.tryResolveDesignHelpProposal();
    this.tryResumeSavedDesign();
    if (this.door && this.door.kind === "door") this.restoreReadyGallery(this.door);
  };

  Take.prototype.stateFor = function (slide) {
    var s = this.state[slide.handle];
    if (!s) {
      var draft = readTakeDraft(slide.handle, this.draftOwner);
      var choices = draft && draft.options ? copy(draft.options) : {};
      s = this.state[slide.handle] = {
        options: slide.intake ? optionsWithDraft(slide.intake, choices) : {},
        draftOptions: choices,
        motif: draft && typeof draft.motif === "string" ? draft.motif : null,
        stills: [],
        selected: null,
        selectedReady: false,
        visibleStill: null,
        compiledPrompt: "",
        designInput: null,
        designSummary: null,
        traceId: "",
        revisionId: "",
        attemptId: "",
        lastFailureTraceId: "",
        lastFailureAttemptId: "",
        unresolvedAttempt: false,
        recoveryPartial: false,
        recoveryPending: false,
        pilotReviewState: "",
        pilotDraftLoaded: false,
        pilotDraftEditable: false,
        pilotObjectUrls: [],
        galleryRestoreStarted: false,
        recoveryPendingRequestId: "",
        recoveryError: "",
        finishableRequestId: "",
        savedDesignConflict: false,
        savedDesignChanged: false,
        helpTraceId: "",
        handoffReason: "VOLUNTARY",
        acceptedHelpReference: "",
        acceptedHelpTraceId: "",
        acceptedHelpParentAttemptId: "",
        layer: 0,
        stillPaintToken: 0
      };
      if (draft && typeof draft.motif === "string") s.motif = draft.motif;
    }
    return s;
  };

  Take.prototype.anyStill = function () {
    var self = this;
    return Object.keys(this.state).some(function (h) { return self.state[h].stills.length > 0; });
  };

  Take.prototype.figureFor = function (slide) {
    var figs = this.carousel ? this.carousel.figures : [];
    for (var i = 0; i < figs.length; i++) {
      if (figs[i].getAttribute("data-handle") === slide.handle) return figs[i];
    }
    return null;
  };

  Take.prototype.bind = function () {
    var self = this;

    root.document.addEventListener("variant:update", function (event) {
      self.onVariantUpdate(event);
    });

    if (this.fields) {
      this.fields.addEventListener("click", function (e) {
        var chip = e.target.closest("[data-chip-key]");
        if (!chip || self.busy || !self.door || self.door.kind !== "door") return;
        var key = chip.getAttribute("data-chip-key");
        var value = chip.getAttribute("data-chip-value");
        var st = self.stateFor(self.door);
        st.options[key] = value;
        st.draftOptions[key] = value;
        if (key === "pendant" && value === "no") delete st.options.bail;
        if (key === "pendant" && value === "yes" && !st.options.bail) st.options.bail = "fixed bail";
        writeTakeDraft(self.door.handle, self.draftOwner, st.motif, st.draftOptions);
        self.renderFields();
      });
    }

    if (this.motif) {
      this.motif.setAttribute("maxlength", String(USER_PROMPT_MAX));
      this.motif.addEventListener("input", function () {
        if (self.door && self.door.kind === "door") {
          var st = self.stateFor(self.door);
          st.motif = self.motif.value;
          writeTakeDraft(self.door.handle, self.draftOwner, st.motif, st.draftOptions);
        }
        self.refreshSavedDesignCompatibility();
        self.syncTake();
      });
    }

    if (this.take) {
      this.take.addEventListener("click", function () { self.onTake(); });
    }
    if (this.recoveryButton) {
      this.recoveryButton.addEventListener("click", function () { self.recoverGeneration(); });
    }
    if (this.resumeButton) {
      this.resumeButton.addEventListener("click", function () { self.resumeApprovedGeneration(); });
    }
    if (this.finishImagesButton) {
      this.finishImagesButton.addEventListener("click", function () { self.finishSavedImages(); });
    }
    if (this.savedDesignRetryButton) {
      this.savedDesignRetryButton.addEventListener("click", function () { self.tryResumeSavedDesign(true); });
    }
    if (this.designProposalAccept) {
      this.designProposalAccept.addEventListener("click", function () { self.acceptDesignHelpProposal(); });
    }
    if (this.designProposalDismiss) {
      this.designProposalDismiss.addEventListener("click", function () { self.dismissDesignHelpProposal(); });
    }

    if (this.alternate) {
      this.alternate.addEventListener("click", function () { self.swapAlternate(); });
    }

    if (this.bag) {
      this.bag.addEventListener("click", function () { self.onAddToBag(); });
    }

    if (this.save) {
      this.save.addEventListener("click", function () { self.onSave(); });
    }

    if (this.asShownButton) {
      this.asShownButton.addEventListener("click", function () { self.onAddAsShown(); });
    }
    if (this.asShownLink) {
      this.asShownLink.addEventListener("click", function () { self.onAddAsShown(); });
    }

    this.el.addEventListener("contextmenu", function (e) {
      if (e.target && e.target.tagName === "IMG") e.preventDefault();
    });
    this.el.addEventListener("dragstart", function (e) {
      if (e.target && e.target.tagName === "IMG") e.preventDefault();
    });
  };

  Take.prototype.onVariantUpdate = function (event) {
    if (!this.cfg.onProductPage || !event || !event.detail || !event.detail.data) return;
    var data = event.detail.data;
    var productId = String(data.productId || "");
    if (!productId || data.newProduct) return;
    var slide = null;
    for (var i = 0; i < this.slides.length; i++) {
      if (String(this.slides[i].id) === productId) { slide = this.slides[i]; break; }
    }
    if (!slide) return;

    var target = event.target;
    var section = target && typeof target.closest === "function" ? target.closest(".shopify-section") : null;
    var nativeVariantId = "";
    if (section) {
      var forms = section.querySelectorAll("product-form-component[data-product-id]");
      for (var j = 0; j < forms.length; j++) {
        if (String(forms[j].getAttribute("data-product-id") || "") !== productId) continue;
        var input = forms[j].querySelector('input[name="id"]');
        if (input) { nativeVariantId = String(input.value || ""); break; }
      }
    }

    var resource = event.detail.resource;
    var eventVariantId = resource && /^[1-9]\d{0,19}$/.test(String(resource.id || "")) ? String(resource.id) : "";
    var variant = eventVariantId ? variantForSlide(slide, eventVariantId) : null;
    if (!variant || !nativeVariantId || nativeVariantId !== eventVariantId
      || typeof resource.available !== "boolean" || typeof variant.available !== "boolean"
      || typeof variant.price !== "string") {
      slide.variantId = "";
      slide.variantAvailable = false;
      slide.price = "";
    } else {
      slide.variantId = eventVariantId;
      slide.variantAvailable = resource.available === true && variant.available === true;
      slide.price = variant.price;
    }
    if (this.door === slide) this.paintPanel(slide, null);
  };

  Take.prototype.updateDesignHelpProposalView = function () {
    if (!this.designProposal) return;
    var state = this.helpProposalStatus;
    this.designProposal.hidden = state === "NONE" || state === "CONSUMED";
    if (this.designProposalStatus) {
      var messages = {
        INVALID: "This design proposal link is incomplete. You can continue without it or contact the shop owner for personal assistance.",
        PENDING: "Verifying this proposal with the account that owns the saved design…",
        LOADING: "Verifying this proposal with the account that owns the saved design…",
        ERROR: "This design proposal is unavailable to this account or browser session. Sign in to the owning account or reopen it in the original browser.",
        ACCEPTING: "Recording your approval…",
        READY: "Review the original request and the proposed changes below. Nothing will be generated until you accept.",
        ACCEPTED: "You accepted this revision. Review the applied choices, then choose Take when you are ready.",
      };
      this.designProposalStatus.textContent = messages[state] || "";
    }
    var proposal = this.helpProposal;
    if (proposal) {
      if (this.designProposalClarification) this.designProposalClarification.textContent = proposal.staff_clarification;
      if (this.designProposalOriginalText) this.designProposalOriginalText.textContent = proposal.original_submitted_text;
      if (this.designProposalOriginalOptions) this.designProposalOriginalOptions.textContent = proposal.original_options_display;
      if (this.designProposalText) this.designProposalText.textContent = proposal.submitted_text;
      if (this.designProposalOptions) this.designProposalOptions.textContent = proposal.proposed_options_display;
    }
    if (this.designProposalAccept) {
      this.designProposalAccept.hidden = state !== "READY";
      this.designProposalAccept.disabled = state === "ACCEPTING";
    }
    if (this.designProposalDismiss) this.designProposalDismiss.hidden = state === "CONSUMED" || state === "NONE";
  };

  Take.prototype.dismissDesignHelpProposal = function () {
    if (this.helpProposalStatus === "ACCEPTING") return;
    try {
      var url = new root.URL(root.location.href);
      url.searchParams.delete("auriq_help_proposal");
      root.history.replaceState(root.history.state, "", url.pathname + url.search + url.hash);
    } catch (e) { /* The current page can continue even when history is unavailable. */ }
    this.helpProposalQuery = { present: false, valid: true, reference: "" };
    this.helpProposalStatus = "NONE";
    this.helpProposal = null;
    if (this.door) {
      var st = this.stateFor(this.door);
      if (this.helpProposalStatus === "ACCEPTED" && this.helpProposal) {
        st.options = optionsWithDraft(this.door.intake, this.helpProposal.original_options);
        st.draftOptions = copy(st.options);
        st.motif = this.helpProposal.original_submitted_text;
        if (this.motif) this.motif.value = st.motif;
        this.renderFields();
      }
      if (st.traceId === st.acceptedHelpTraceId && !st.selected) {
        st.traceId = "";
        st.revisionId = "";
        st.attemptId = "";
      }
      st.acceptedHelpReference = "";
      st.acceptedHelpTraceId = "";
      st.acceptedHelpParentAttemptId = "";
      st.helpTraceId = "";
    }
    this.updateDesignHelpProposalView();
    this.syncTake();
  };

  Take.prototype.completeAcceptedDesignHelpProposal = function (state) {
    if (!state || !state.acceptedHelpReference) return;
    var completedReference = state.acceptedHelpReference;
    state.acceptedHelpReference = "";
    state.acceptedHelpTraceId = "";
    state.acceptedHelpParentAttemptId = "";
    if (this.helpProposalQuery.reference === completedReference) {
      try {
        var url = new root.URL(root.location.href);
        url.searchParams.delete("auriq_help_proposal");
        root.history.replaceState(root.history.state, "", url.pathname + url.search + url.hash);
      } catch (e) { /* Keep the completed page usable when history is unavailable. */ }
      this.helpProposalQuery = { present: false, valid: true, reference: "" };
      this.helpProposalStatus = "CONSUMED";
      this.helpProposal = null;
      this.updateDesignHelpProposalView();
    }
  };

  Take.prototype.tryResolveDesignHelpProposal = function () {
    var self = this;
    if (!this.helpProposalQuery.present || this.helpProposalResolveStarted || !this.configResolved) return Promise.resolve();
    this.helpProposalResolveStarted = true;
    if (!this.helpProposalQuery.valid || !this.sheet || !this.door || this.door.kind !== "door" || !this.cfg.appUrl) {
      this.helpProposalStatus = "INVALID";
      this.updateDesignHelpProposalView();
      this.syncTake();
      return Promise.resolve();
    }
    this.helpProposalStatus = "LOADING";
    this.updateDesignHelpProposalView();
    this.syncTake();
    var visitorReady = this.cfg.customerId ? Promise.resolve(null) : ensureVisitorForExplicitAction();
    return visitorReady.then(function (visitor) {
      if (!self.cfg.customerId && !visitor) throw new Error("owner session unavailable");
      var body = { action: "resolve", proposal_reference: self.helpProposalQuery.reference };
      if (visitor) {
        body.visitor_id = visitor.id;
        body.visitor_hmac = visitor.hmac;
      }
      return root.fetch(self.cfg.appUrl.replace(/\/$/, "") + "/api/design-resume", {
        method: "POST",
        credentials: "same-origin",
        referrerPolicy: "no-referrer",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body)
      }).then(function (response) {
        return response.json().catch(function () { return {}; }).then(function (data) {
          return { response: response, data: data };
        });
      });
    }).then(function (result) {
      var proposal = result.data && result.data.proposal;
      if (!result.response.ok || result.data.success !== true || !proposal
        || proposal.revision_id !== self.helpProposalQuery.reference
        || !UUID_RE.test(proposal.trace_id || "") || !UUID_RE.test(proposal.source_help_reference || "")
        || proposal.proposal_latest !== true || typeof proposal.accepted !== "boolean"
        || typeof proposal.original_submitted_text !== "string"
        || typeof proposal.submitted_text !== "string" || !motifValid(proposal.submitted_text)
        || typeof proposal.staff_clarification !== "string" || !proposal.staff_clarification.trim()) {
        throw new Error("proposal could not be verified");
      }
      var original = parseHelpDesignContext(proposal.original_options_json);
      var proposed = parseHelpDesignContext(proposal.normalized_options_json);
      var door = self.door;
      if (!door || !door.intake || !sameProductId(original.productId, proposed.productId)
        || original.formType !== proposed.formType || original.pair !== proposed.pair
        || !sameProductId(proposed.productId, door.id)
        || proposed.formType !== door.intake.formType || proposed.pair !== !!door.intake.pair
        || !sameSupportedOptions(door.intake, proposed.options)) {
        throw new Error("proposal no longer matches the current product setup");
      }
      if (proposal.selected_attempt_id && !UUID_RE.test(proposal.selected_attempt_id)) throw new Error("proposal lineage is invalid");
      self.helpProposal = {
        trace_id: proposal.trace_id,
        revision_id: proposal.revision_id,
        source_help_reference: proposal.source_help_reference,
        selected_attempt_id: proposal.selected_attempt_id || "",
        staff_clarification: proposal.staff_clarification,
        original_submitted_text: proposal.original_submitted_text,
        original_options: original.options,
        original_options_display: JSON.stringify(original.options, null, 2),
        submitted_text: proposal.submitted_text,
        proposed_options_display: JSON.stringify(proposed.options, null, 2),
        options: proposed.options,
        accepted: proposal.accepted,
      };
      self.helpProposalStatus = proposal.accepted ? "ACCEPTED" : "READY";
      self.updateDesignHelpProposalView();
      if (proposal.accepted) self.applyAcceptedDesignHelpProposal();
      else self.syncTake();
    }).catch(function () {
      self.helpProposalStatus = "ERROR";
      self.updateDesignHelpProposalView();
      self.syncTake();
    });
  };

  Take.prototype.applyAcceptedDesignHelpProposal = function () {
    if (!this.door || !this.helpProposal) return;
    var st = this.stateFor(this.door);
    st.options = copy(this.helpProposal.options);
    st.draftOptions = copy(this.helpProposal.options);
    st.motif = this.helpProposal.submitted_text;
    st.traceId = this.helpProposal.trace_id;
    st.revisionId = "";
    st.attemptId = "";
    st.helpTraceId = this.helpProposal.trace_id;
    st.acceptedHelpReference = this.helpProposal.revision_id;
    st.acceptedHelpTraceId = this.helpProposal.trace_id;
    st.acceptedHelpParentAttemptId = this.helpProposal.selected_attempt_id;
    if (this.motif) this.motif.value = st.motif;
    this.renderFields();
    this.syncTake();
  };

  Take.prototype.acceptDesignHelpProposal = function () {
    var self = this;
    if (this.helpProposalStatus !== "READY" || !this.helpProposal || !this.helpProposalQuery.reference
      || this.helpProposalAccept && this.helpProposalAccept.disabled) return Promise.resolve();
    if (!root.crypto || typeof root.crypto.randomUUID !== "function") {
      this.helpProposalStatus = "ERROR";
      this.updateDesignHelpProposalView();
      this.syncTake();
      return Promise.resolve();
    }
    if (!this.helpProposalAcceptEventId) this.helpProposalAcceptEventId = root.crypto.randomUUID();
    if (!UUID_RE.test(this.helpProposalAcceptEventId)) {
      this.helpProposalStatus = "ERROR";
      this.updateDesignHelpProposalView();
      this.syncTake();
      return Promise.resolve();
    }
    this.helpProposalStatus = "ACCEPTING";
    this.updateDesignHelpProposalView();
    this.syncTake();
    var visitorReady = this.cfg.customerId ? Promise.resolve(null) : ensureVisitorForExplicitAction();
    return visitorReady.then(function (visitor) {
      if (!self.cfg.customerId && !visitor) throw new Error("owner session unavailable");
      var body = {
        action: "accept",
        proposal_reference: self.helpProposalQuery.reference,
        event_id: self.helpProposalAcceptEventId,
      };
      if (visitor) {
        body.visitor_id = visitor.id;
        body.visitor_hmac = visitor.hmac;
      }
      return root.fetch(self.cfg.appUrl.replace(/\/$/, "") + "/api/design-resume", {
        method: "POST",
        credentials: "same-origin",
        referrerPolicy: "no-referrer",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body)
      }).then(function (response) {
        return response.json().catch(function () { return {}; }).then(function (data) {
          return { response: response, data: data };
        });
      });
    }).then(function (result) {
      if (!result.response.ok || result.data.success !== true || result.data.accepted !== true
        || result.data.accepted_help_reference !== self.helpProposalQuery.reference) {
        throw new Error("acceptance could not be confirmed");
      }
      self.helpProposal.accepted = true;
      self.helpProposalStatus = "ACCEPTED";
      self.applyAcceptedDesignHelpProposal();
      self.updateDesignHelpProposalView();
      self.setStatus("Your accepted design update is ready. Choose Take when you are ready to create its image.");
    }).catch(function () {
      self.helpProposalStatus = "READY";
      self.updateDesignHelpProposalView();
      if (self.designProposalStatus) {
        self.designProposalStatus.textContent = "We could not confirm your approval. You can retry safely; your review remains available.";
      }
      self.syncTake();
    });
  };

  // Called by the carousel after every move. The panel words fade out, swap
  // 200 ms into the ring move, and fade back in as the piece settles.
  Take.prototype.onSlide = function (index, immediate) {
    var slide = this.slides[index];
    if (!slide) return;
    var self = this;
    var previous = this.door;
    if (previous && previous !== slide && previous.kind === "door" && this.motif) {
      this.stateFor(previous).motif = this.motif.value;
    }
    if (previous && previous !== slide && previous.kind === "door") {
      var previousState = this.stateFor(previous);
      this.invalidateStillPaint(previousState);
      previousState.selectedReady = false;
      this.paintStillActions(previousState);
    }
    if (previous && previous !== slide) this.paintDesignSummary(null);
    this.door = slide;
    this.setStatus("");
    if (this.swapTimer) { root.clearTimeout(this.swapTimer); this.swapTimer = null; }
    if (immediate || !previous || previous === slide) {
      this.paintPanel(slide, previous);
      return;
    }
    this.el.classList.add("is-switching");
    this.swapTimer = root.setTimeout(function () {
      self.swapTimer = null;
      if (self.door !== slide) return;
      self.paintPanel(slide, previous);
      self.el.classList.remove("is-switching");
    }, PANEL_SWAP_AT_MS);
  };

  Take.prototype.paintPanel = function (slide, previous) {
    if (this.eyebrow) this.eyebrow.textContent = slide.collection || "";
    if (this.title) this.title.textContent = slide.title || "";
    if (this.price) this.price.textContent = slide.price || "";

    var isDoor = slide.kind === "door";
    var isLook = slide.kind === "look";
    if (this.doorPanel) this.doorPanel.hidden = !isDoor;
    if (this.lookPanel) this.lookPanel.hidden = !isLook;
    if (this.view) {
      if (slide.url) this.view.setAttribute("href", slide.url);
      else this.view.removeAttribute("href");
    }

    if (!isDoor) {
      if (this.fields) this.fields.innerHTML = "";
      if (this.after) this.after.hidden = true;
      if (this.alternate) this.alternate.hidden = true;
      this.paintDesignSummary(null);
      this.paintAsShown();
      this.paintChrome();
      this.syncTake();
      if (previous && previous !== slide) track("customizer_open", productCtx(slide));
      return;
    }

    var st = this.stateFor(slide);
    if (this.motif) {
      this.motif.placeholder = slide.intake.motifPlaceholder || "";
      if (st.motif !== null) this.motif.value = st.motif;
    }
    if (this.take) {
      this.take.textContent = st.stills.length
        ? (this.take.getAttribute("data-label-again") || "Take again")
        : (this.take.getAttribute("data-label-take") || "Take");
    }
    this.renderFields();
    this.paintStill();
    this.paintAsShown();
    this.paintChrome();
    this.syncTake();
    this.refreshGenerationRecovery();
    if (previous && previous !== slide) track("customizer_open", productCtx(slide));
  };

  // Product page only: the piece as shown can always go in the bag. It is the
  // black button until a still exists; then the still's Add to bag is primary
  // and the original becomes a small text link.
  // On the spec sheet the link is the resting state of a configured piece.
  // The black button is primary only when the piece is not a door (as shown,
  // or Take unavailable).
  Take.prototype.paintAsShown = function () {
    if (!this.asShown || !this.door) return;
    var st = this.door.kind === "door" ? this.stateFor(this.door) : null;
    var hasStill = !!(st && st.visibleStill);
    var canPurchase = !!purchasableVariant(this.door);
    if (this.asShownButton) this.asShownButton.disabled = !canPurchase;
    if (this.asShownLink) this.asShownLink.setAttribute("aria-disabled", canPurchase ? "false" : "true");
    if (this.sheet) {
      var primary = this.door.kind !== "door";
      if (this.asShownButton) this.asShownButton.hidden = !primary;
      if (this.asShownLink) this.asShownLink.hidden = primary;
      this.asShown.classList.toggle("is-link", !primary);
      return;
    }
    if (this.asShownButton) this.asShownButton.hidden = hasStill;
    if (this.asShownLink) this.asShownLink.hidden = !hasStill;
    this.asShown.classList.toggle("is-link", hasStill);
  };

  // Spec sheet chrome: price label, unavailable line, description link,
  // and the canvas reference / still state.
  Take.prototype.paintChrome = function () {
    if (!this.sheet || !this.door) return;
    var kind = this.door.kind;
    var isDoor = kind === "door";
    if (this.priceNote) this.priceNote.hidden = !isDoor;
    if (this.unavailable) this.unavailable.hidden = kind !== "unavailable";
    if (this.about) this.about.hidden = isDoor;
    if (this.take) this.take.hidden = kind === "unavailable";
    this.paintCanvas();
  };

  Take.prototype.paintCanvas = function () {
    if (!this.sheet || !this.door) return;
    var slide = this.door;
    var st = slide.kind === "door" ? this.state[slide.handle] : null;
    var hasStill = !!(st && st.visibleStill);
    this.el.classList.toggle("has-still", hasStill);
    if (this.canvas) this.canvas.classList.toggle("is-reference", !hasStill);
    if (this.corner) this.corner.hidden = !hasStill;
    if (this.caption) {
      this.caption.hidden = hasStill;
      if (!hasStill) {
        var plain = slide.kind === "look" || slide.kind === "unavailable";
        this.caption.textContent = plain ? CAPTION_AS_SHOWN : CAPTION_REFERENCE;
      }
    }
    var src = slide.image && slide.image.src;
    if (src && this.references) {
      for (var i = 0; i < this.references.length; i++) {
        if (this.references[i].getAttribute("src") !== src) this.references[i].src = src;
      }
    }
  };

  // After a Take on a phone, bring the still into view. Desktop stays put.
  Take.prototype.revealCanvas = function () {
    if (!this.sheet || !this.canvas) return;
    var w = root.innerWidth || 0;
    if (!(w > 0 && w <= MOBILE_MAX)) return;
    if (typeof this.canvas.scrollIntoView !== "function") return;
    try {
      this.canvas.scrollIntoView({ block: "start", behavior: reducedMotion() ? "auto" : "smooth" });
    } catch (e) {
      this.canvas.scrollIntoView();
    }
  };

  Take.prototype.addNativeCartLine = function (properties) {
    var door = this.door;
    var variant = purchasableVariant(door);
    var variantId = variant ? String(variant.id) : "";
    if (!variantId) {
      this.setStatus("This piece cannot be added right now.");
      this.setBusy(false);
      return;
    }
    var self = this;
    var mutation = root.AuriqCartMutation;
    if (!mutation || typeof mutation.run !== "function" || typeof mutation.updated !== "function") {
      this.setStatus("Shopify could not prepare a safe bag update. Continue with the owner for assistance.");
      this.setBusy(false);
      return;
    }
    var add = function (sequence) {
      var currentVariant = purchasableVariant(door);
      if (!currentVariant || String(currentVariant.id) !== variantId) {
        return Promise.reject(new Error("The product option changed. Review it before adding this piece to your bag."));
      }
      return readCartSnapshot().then(function (before) {
        var beforeQuantity = cartQuantity(before, variantId);
        return root.fetch("/cart/add.js", {
          method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: variantId, quantity: 1, ...(properties && Object.keys(properties).length ? { properties: properties } : {}) })
        }).then(function (response) {
          if (!response.ok) throw new Error("Shopify could not add this piece to your bag.");
          return response.json();
        }).then(function (cartLine) {
          mutation.updated(cartLine, "auriq-take", {
            source: "product-form-component", itemCount: 1, productId: String(door.id),
            variantId: variantId, cartUpdateSequence: sequence
          });
        }).catch(function () {
          return readCartSnapshot().then(function (after) {
            if (cartQuantity(after, variantId) <= beforeQuantity) {
              throw new Error("We couldn't confirm whether this piece was added. Check your bag before trying again.");
            }
            mutation.updated(after, "auriq-take", {
              source: "auriq-take", itemCount: after.item_count, productId: String(door.id),
              variantId: variantId, cartUpdateSequence: sequence
            });
          }, function () {
            throw new Error("We couldn't confirm whether this piece was added. Check your bag before trying again.");
          });
        });
      });
    };
    return mutation.run(add).then(function () {
      track("add_to_cart", withCtx(door, { quantity: 1, variant_id: variantId }));
      Take.navigate("/cart");
    }).catch(function (err) {
      self.setStatus(err && err.message ? err.message : "Could not add to bag.");
      self.setBusy(false);
    });
  };

  Take.prototype.addDesignReferenceToCart = function (door, st, still, designLabel) {
    var help = this.designHelpApi || this.designHelp && this.designHelp.__auriqDesignHelp;
    var variant = purchasableVariant(door);
    var variantId = variant ? String(variant.id) : "";
    var mutation = root.AuriqCartMutation;
    if (!help || typeof help.saveContext !== "function" || !variantId
      || !mutation || typeof mutation.run !== "function" || typeof mutation.updated !== "function") {
      this.setStatus("We could not prepare a private design reference for checkout. Continue with the owner to arrange your design.");
      this.setBusy(false);
      return;
    }
    var self = this;
    this.setBusy(true);
    this.setStatus("");
    Promise.resolve().then(function () {
      if (self.cfg.customerId) return self.claimSelectedStill(door, still);
    }).then(function () {
      var currentVariant = purchasableVariant(door);
      if (self.door !== door || st.selected !== still || !currentVariant || String(currentVariant.id) !== variantId) {
        throw new Error("The selected design or product option changed. Review it before continuing.");
      }
      return help.saveContext();
    }).then(function (saved) {
      var reference = saved && saved.data && saved.data.help_reference;
      var currentVariant = purchasableVariant(door);
      if (!UUID_RE.test(reference || "") || self.door !== door || st.selected !== still
        || !currentVariant || String(currentVariant.id) !== variantId) {
        throw new Error("The design context could not be linked to this selection. Review it before continuing.");
      }
      var add = function (sequence) {
        var currentVariant = purchasableVariant(door);
        if (!currentVariant || String(currentVariant.id) !== variantId) {
          return Promise.reject(new Error("The product option changed. Review it before adding this design to your bag."));
        }
        return readCartSnapshot().then(function (before) {
          var beforeQuantity = cartQuantity(before, variantId, "_AURIQ Design Reference", reference);
          return root.fetch("/cart/add.js", {
            method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: variantId, quantity: 1, properties: {
              "_AURIQ Design Reference": reference,
              "_Custom Design": designLabel
            } })
          }).then(function (response) {
            if (!response.ok) throw new Error("Shopify could not add this design to your bag.");
            return response.json();
          }).then(function (cartLine) {
            mutation.updated(cartLine, "auriq-take", {
              source: "product-form-component", itemCount: 1, productId: String(door.id),
              variantId: variantId, cartUpdateSequence: sequence
            });
          }).catch(function () {
            return readCartSnapshot().then(function (after) {
              if (cartQuantity(after, variantId, "_AURIQ Design Reference", reference) <= beforeQuantity) {
                throw new Error("We couldn't confirm whether this design was added. Check your bag before trying again.");
              }
              mutation.updated(after, "auriq-take", {
                source: "auriq-take", itemCount: after.item_count, productId: String(door.id),
                variantId: variantId, cartUpdateSequence: sequence
              });
            }, function () {
              throw new Error("We couldn't confirm whether this design was added. Check your bag before trying again.");
            });
          });
        });
      };
      return mutation.run(add);
    }).then(function () {
      Take.navigate("/cart");
    }).catch(function (error) {
      self.setStatus(error && error.message ? error.message : "This design was not added to checkout. Continue with the owner for assistance.");
      self.setBusy(false);
    });
  };

  Take.prototype.onAddAsShown = function () {
    if (this.busy || !this.door) return;
    this.setBusy(true);
    this.setStatus("");
    this.addNativeCartLine({});
  };

  Take.prototype.renderFields = function () {
    if (!this.fields || !this.door || this.door.kind !== "door") return;
    var self = this;
    var door = this.door.intake;
    var st = this.stateFor(this.door);
    var allowed = door.clientFields;
    this.fields.innerHTML = "";
    groupsFor(door, st.options).forEach(function (g) {
      if (allowed && allowed.indexOf(g.key) === -1) return;
      // A group with one choice is not a choice; the value still travels in
      // options (e.g. the locked pendant on the pendant doors).
      if (g.items.length < 2) return;
      var row = document.createElement("div");
      row.className = "auriq-take__row";
      row.setAttribute("role", "group");
      var lab = document.createElement("span");
      lab.className = "auriq-take__label";
      lab.textContent = g.label;
      row.appendChild(lab);
      var wrap = document.createElement("div");
      wrap.className = "auriq-take__chips";
      g.items.forEach(function (item) {
        var b = document.createElement("button");
        b.type = "button";
        var on = st.options[g.key] === item.value;
        b.className = "auriq-take__chip" + (on ? " is-on" : "");
        b.setAttribute("data-chip-key", g.key);
        b.setAttribute("data-chip-value", item.value);
        b.setAttribute("aria-pressed", on ? "true" : "false");
        b.textContent = item.label;
        wrap.appendChild(b);
      });
      row.appendChild(wrap);
      self.fields.appendChild(row);
    });
    this.refreshSavedDesignCompatibility();
  };

  Take.prototype.motifText = function () {
    return this.motif ? String(this.motif.value || "") : "";
  };

  Take.prototype.syncTake = function () {
    if (this.count && this.motif) this.count.textContent = String(this.motif.value.length);
    if (!this.take) return;
    var ok = this.motifText().trim().length <= USER_PROMPT_MAX;
    var out = this.available !== null && this.available <= 0;
    var isDoor = !!(this.door && this.door.kind === "door");
    var st = isDoor ? this.stateFor(this.door) : null;
    this.take.hidden = out || !isDoor;
    var proposalLocked = this.helpProposalStatus !== "NONE" && this.helpProposalStatus !== "CONSUMED"
      && this.helpProposalStatus !== "ACCEPTED";
    var pilotRequestLocked = !!(st && PILOT_INTENT_STATES.concat(PILOT_IMAGE_STATES, PILOT_STATUS_ONLY_STATES, LEGACY_PILOT_STATES).indexOf(st.pilotReviewState) !== -1);
    this.take.disabled = this.busy || this.recoveryBusy || !ok || out || !isDoor || proposalLocked
      || pilotRequestLocked || !!(st && st.savedDesignConflict);
  };

  Take.prototype.setStatus = function (text) {
    if (this.status) this.status.textContent = text || "";
  };

  Take.prototype.showLoginPrompt = function (text) {
    if (!this.status) return;
    this.status.replaceChildren(root.document.createTextNode(text + " "));
    var link = root.document.createElement("a");
    link.href = "/account/login?return_url=" + encodeURIComponent(root.location.pathname + root.location.search);
    link.textContent = "Log in";
    this.status.appendChild(link);
  };

  Take.prototype.setQuota = function (remaining, cap, resetsAt, available, pending, trafficThrottled) {
    if (typeof remaining === "number") this.remaining = remaining;
    if (typeof cap === "number") this.cap = cap;
    if (resetsAt) this.resetsAt = resetsAt;
    if (typeof pending === "number") this.pending = pending;
    if (typeof trafficThrottled === "boolean") this.trafficThrottled = trafficThrottled;
    if (typeof available === "number") this.available = available;
    else if (this.trafficThrottled) this.available = 0;
    else if (typeof remaining === "number") this.available = Math.max(0, remaining - this.pending);
    if (this.quota) {
      if (this.remaining === null) {
        this.quota.textContent = this.trafficThrottled
          ? "A temporary shared traffic limit is active. Try again later or contact the shop representative."
          : this.pending > 0
          ? "A saved request is still being checked. Contact the shop representative if you need help."
          : this.cap !== null
            ? "Limited trial: up to " + this.cap + " concept actions in 24 hours. Exact availability is checked when you create a concept."
            : "Limited design trial. Exact availability is checked when you create a concept.";
      } else if (this.available !== null && this.available <= 0 && this.pending > 0) {
        this.quota.textContent = this.remaining + " completed design attempt" + (this.remaining === 1 ? "" : "s")
          + " remain; a saved request is still being checked. Contact the shop representative if you need help.";
      } else if (this.available !== null && this.available <= 0 && this.trafficThrottled && this.remaining > 0) {
        this.quota.textContent = "A temporary shared traffic limit is active. " + this.remaining + " completed design attempt"
          + (this.remaining === 1 ? " remains" : "s remain") + "; try again later or contact the shop representative.";
      } else if (this.available !== null && this.available <= 0) {
        this.quota.textContent = "Your 24-hour design allowance is used. Contact the shop representative to refine your design and arrange your order.";
      } else if (this.cap !== null) {
        this.quota.textContent = this.remaining + " of " + this.cap + " completed design attempts remain in this 24-hour window."
          + (this.pending > 0 ? " " + this.pending + " saved request" + (this.pending === 1 ? " is" : "s are")
            + " still being checked; " + this.available + " available now." : "");
      } else {
        this.quota.textContent = this.remaining + " design attempts remaining in this 24-hour window.";
      }
    }
    this.syncTake();
  };

  Take.prototype.refreshQuota = function () {
    var self = this;
    if (!this.cfg.appUrl || !this.quota || this.quotaRequested) return Promise.resolve();
    var hasDoor = this.slides.some(function (s) { return s.kind === "door"; });
    if (!hasDoor) return Promise.resolve();
    this.quotaRequested = true;
    var body = {};
    var visitor = readVisitor();
    if (!this.cfg.customerId && !visitor) return Promise.resolve();
    if (visitor && visitor.id && visitor.hmac) {
      body.visitor_id = visitor.id;
      body.visitor_hmac = visitor.hmac;
    }
    return root.fetch(self.cfg.appUrl + "/api/generate-quota", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      }).then(function (r) {
        if (!r.ok) return null;
        return r.json();
      }).then(function (data) {
        if (!data || typeof data.remaining !== "number") return;
        self.setQuota(data.remaining, data.cap, data.resetsAt, data.available, data.pending, data.traffic_throttled === true);
      }).catch(function () { /* quota line is informational */ });
  };

  Take.prototype.paintDesignSummary = function (st) {
    var container = this.designSummary;
    if (!container) return;
    if (!st || !st.selected) {
      container.hidden = true;
      return;
    }
    var renderer = root.AuriqDesignSummary;
    if (renderer && typeof renderer.render === "function") {
      try {
        var optionSummary = renderer.describeOptions(st.designInput, groupsFor);
        renderer.render(container, {
          designSummary: st.designSummary,
          designInput: st.designInput,
          optionFields: optionSummary.fields,
          optionsComplete: optionSummary.complete
        }, container);
        return;
      } catch (e) {
        // The summary is additive; display failure must not interrupt image selection.
      }
    }
    var title = container.getAttribute("data-design-summary-title") || "Your design";
    var unavailable = container.getAttribute("data-design-summary-unavailable") || "Design details are unavailable.";
    container.textContent = title + "\n" + unavailable;
    container.hidden = false;
  };

  Take.prototype.invalidateStillPaint = function (st) {
    st.stillPaintToken = (st.stillPaintToken || 0) + 1;
    return st.stillPaintToken;
  };

  Take.prototype.selectedStillMatchesAttempt = function (st) {
    return !st || !st.selected || st.stills.indexOf(st.selected) !== -1;
  };

  Take.prototype.paintGallery = function (st) {
    if (!this.gallery) return;
    var items = st && Array.isArray(st.stills) ? st.stills : [];
    this.gallery.hidden = items.length === 0;
    if (!items.length) { this.gallery.replaceChildren(); this.gallery.__auriqAssets = ""; return; }
    var assetKey = items.map(function (item) { return item.asset_id || item.url; }).join("|");
    var sameItems = this.gallery.__auriqAssets === assetKey;
    var self = this;
    if (!sameItems) {
      this.gallery.replaceChildren();
      items.forEach(function (still, index) {
        var card = root.document.createElement("div");
        card.className = "auriq-take__gallery-card";
        var button = root.document.createElement("button");
        button.type = "button";
        button.className = "auriq-take__gallery-select";
        button.setAttribute("data-asset-id", still.asset_id || "");
        button.setAttribute("aria-pressed", still === st.selected ? "true" : "false");
        var image = root.document.createElement("img");
        image.src = still.url;
        image.alt = "Concept preview " + (index + 1);
        image.loading = "lazy";
        var label = root.document.createElement("span");
        label.textContent = "Preview " + (index + 1) + (still === st.selected ? " · selected" : "");
        button.append(image, label);
        button.addEventListener("click", function () {
          st.selected = still;
          st.designInput = still.designInput || st.designInput;
          st.designSummary = still.designSummary || null;
          st.compiledPrompt = still.compiledPrompt || st.compiledPrompt;
          var helper = root.AuriqGenerationRequests;
          if (helper && still.request_id && still.owner_scope && UUID_RE.test(still.asset_id || "")) {
            try {
              still.selection_persisted = !!helper.recordSelection(String(still.product_id || self.door.id), still.request_id,
                still.owner_scope, still.asset_id, { trace_id: still.trace_id, attempt_id: still.attempt_id });
            } catch (error) { still.selection_persisted = false; }
          }
          try { root.sessionStorage.setItem("auriq.design.gallery-selection.v1:" + String(still.product_id || self.door.id),
            JSON.stringify({ request_id: still.request_id, asset_id: still.asset_id })); } catch (error) { /* the private request record remains recoverable */ }
          self.paintStill();
          self.revealCanvas();
        });
        var details = root.document.createElement("details");
        var summary = root.document.createElement("summary");
        summary.textContent = "Choices for this preview";
        var brief = root.document.createElement("pre");
        var input = still.designInput || {};
        var lines = [];
        if (typeof input.userPrompt === "string" && input.userPrompt) lines.push(input.userPrompt);
        Object.keys(input.options || {}).sort().forEach(function (key) { lines.push(key + ": " + input.options[key]); });
        brief.textContent = lines.join("\n");
        details.append(summary, brief);
        card.append(button, details);
        self.gallery.appendChild(card);
      });
      this.gallery.__auriqAssets = assetKey;
    }
    Array.prototype.forEach.call(this.gallery.querySelectorAll("[data-asset-id]"), function (button, index) {
      var selected = items[index] === st.selected;
      button.setAttribute("aria-pressed", selected ? "true" : "false");
      var label = button.querySelector("span");
      if (label) label.textContent = "Preview " + (index + 1) + (selected ? " · selected" : "");
    });
  };

  Take.prototype.paintStillActions = function (st) {
    var selectedReady = !!(st && st.selected && st.selectedReady);
    var historicalSelection = !this.selectedStillMatchesAttempt(st);
    var blocked = !!(st && (st.unresolvedAttempt || st.recoveryPending || historicalSelection));
    var canContinue = selectedReady && !st.recoveryPartial && !blocked;
    if (this.after) this.after.hidden = !canContinue;
    var selectionNotRetained = !!(st && st.selected && st.selected.request_id && st.selected.selection_persisted === false);
    if (this.save) {
      this.save.hidden = !!(st && st.selected && st.selected.private === true);
      this.save.disabled = this.busy || this.recoveryBusy || !selectedReady || !!st.recoveryPartial || selectionNotRetained
        || blocked || !!st.savedDesignConflict || !!st.savedDesignChanged;
      this.save.textContent = this.save.getAttribute("data-label-save") || "Save to collection";
    }
    if (this.bag) {
      var canAddSelected = !!(purchasableVariant(this.door) && st && st.selected && (st.selected.purchase_ready_reference === true
        || st.selected.private === true && st.selected.review_state === "UNREVIEWED_CONCEPT"));
      this.bag.hidden = !canAddSelected;
      this.bag.textContent = st && st.selected && st.selected.review_state === "UNREVIEWED_CONCEPT" ? "Continue to checkout" : "Add to bag";
      this.bag.disabled = this.busy || this.recoveryBusy || !selectedReady || !canAddSelected
        || !!st.recoveryPartial || selectionNotRetained
        || blocked || !!st.savedDesignConflict || !!st.savedDesignChanged;
    }
    this.paintAsShown();
  };

  // Each slide carries its door photo plus two still layers. The selected
  // still fades in over the photo; swapping stills fades between the layers.
  Take.prototype.paintStill = function () {
    var slide = this.door;
    var st = this.stateFor(slide);
    this.paintGallery(st);
    var paintToken = this.invalidateStillPaint(st);
    var fig = this.figureFor(slide);
    var layers = fig ? [fig.querySelector("[data-still='0']"), fig.querySelector("[data-still='1']")] : [];
    var still = st.selected;
    var visibleLayer = layers[st.layer];
    if (fig && layers[0] && layers[1] && still && st.selectedReady && st.visibleStill === still
      && visibleLayer && visibleLayer.classList.contains("is-current")
      && visibleLayer.getAttribute("src") === still.url && visibleLayer.complete && visibleLayer.naturalWidth > 0) {
      this.paintStillActions(st);
      this.paintDesignSummary({ selected: still, designInput: st.designInput, designSummary: st.designSummary });
      this.paintCanvas();
      return;
    }
    st.selectedReady = false;
    this.paintDesignSummary(null);
    this.paintStillActions(st);
    if (!fig) {
      return;
    }
    if (!layers[0] || !layers[1]) return;
    var self = this;
    var summarySnapshot = { designInput: st.designInput, designSummary: st.designSummary };
    var isCurrentSelection = function () {
      return self.door === slide && st.stillPaintToken === paintToken && st.selected === still;
    };
    var failImage = function (image) {
      if (!isCurrentSelection() || image.getAttribute("src") !== still.url) return;
      st.selectedReady = false;
      self.paintStillActions(st);
      self.paintDesignSummary(null);
      self.setStatus("The selected image could not be displayed. Choose another image or make a new image request.");
    };
    var activateImage = function (image, index) {
      if (!isCurrentSelection() || image.getAttribute("src") !== still.url || !image.complete || image.naturalWidth <= 0) return;
      image.classList.add("is-current");
      layers[1 - index].classList.remove("is-current");
      st.layer = index;
      fig.classList.add("has-still");
      st.visibleStill = still;
      st.selectedReady = true;
      self.paintStillActions(st);
      self.paintCanvas();
      self.paintDesignSummary({
        selected: still,
        designInput: summarySnapshot.designInput,
        designSummary: summarySnapshot.designSummary
      });
    };
    if (!still) {
      layers[0].classList.remove("is-current");
      layers[1].classList.remove("is-current");
      fig.classList.remove("has-still");
      st.visibleStill = null;
      if (this.alternate) this.alternate.hidden = true;
      this.paintCanvas();
      return;
    }
    var cur = layers[st.layer];
    var nextIndex = 1 - st.layer;
    var next = layers[nextIndex];
    if (cur.getAttribute("src") !== still.url) {
      if (cur.classList.contains("is-current")) {
        var commit = function () { activateImage(next, nextIndex); };
        next.alt = slide.title || "";
        next.onload = commit;
        next.onerror = function () { failImage(next); };
        next.src = still.url;
        if (next.complete && next.naturalWidth > 0) commit();
      } else {
        cur.alt = slide.title || "";
        cur.onload = function () { activateImage(cur, st.layer); };
        cur.onerror = function () { failImage(cur); };
        cur.src = still.url;
        if (cur.complete && cur.naturalWidth > 0) activateImage(cur, st.layer);
      }
    } else if (!cur.classList.contains("is-current")) {
      cur.onload = function () { activateImage(cur, st.layer); };
      cur.onerror = function () { failImage(cur); };
      if (cur.complete && cur.naturalWidth > 0) activateImage(cur, st.layer);
    } else {
      cur.onload = function () { activateImage(cur, st.layer); };
      cur.onerror = function () { failImage(cur); };
      if (cur.complete && cur.naturalWidth > 0) activateImage(cur, st.layer);
    }

    var other = st.stills.length > 1 ? st.stills[st.stills.indexOf(still) === 0 ? 1 : 0] : null;
    if (this.alternate && this.alternateImg) {
      if (other) {
        this.alternateImg.src = other.url;
        this.alternateImg.alt = "";
        this.alternate.setAttribute("data-alternate-index", String(st.stills.indexOf(other)));
        this.alternate.hidden = false;
      } else {
        this.alternate.hidden = true;
      }
    }
    this.paintCanvas();
  };

  Take.prototype.showStill = function (index) {
    var st = this.stateFor(this.door);
    var still = st.stills[index];
    if (!still) return;
    st.selected = still;
    if (still.request_id && !st.recoveryPartial) {
      try {
        still.selection_persisted = !!root.AuriqGenerationRequests.recordSelection(
          String(this.door.id), still.request_id, still.owner_scope, still.asset_id, {
            trace_id: still.trace_id,
            attempt_id: still.attempt_id
          }
        );
      } catch (e) {
        still.selection_persisted = false;
      }
      if (!still.selection_persisted) this.setStatus("We could not retain this image selection for recovery. Please try again.");
    }
    this.paintStill();
    this.setBusy(this.busy);
  };

  Take.prototype.swapAlternate = function () {
    if (!this.alternate || this.busy || !this.door || this.door.kind !== "door") return;
    var idx = parseInt(this.alternate.getAttribute("data-alternate-index") || "1", 10);
    this.showStill(idx);
    track("customizer_image_select", withCtx(this.door, { image_index: idx }));
  };

  Take.prototype.setBusy = function (on) {
    this.busy = on;
    this.el.classList.toggle("is-busy", on);
    var st = this.door ? this.stateFor(this.door) : null;
    var proposalReviewLocked = ["PENDING", "LOADING", "READY", "ACCEPTING", "ACCEPTED", "INVALID", "ERROR"].indexOf(this.helpProposalStatus) !== -1;
    if (this.motif) this.motif.disabled = on || proposalReviewLocked;
    if (this.fields) {
      Array.prototype.forEach.call(this.fields.querySelectorAll("[data-chip-key]"), function (chip) {
        chip.disabled = on || proposalReviewLocked;
      });
    }
    var selectionNotRetained = !!(st && st.selected && st.selected.request_id && st.selected.selection_persisted === false);
    var historicalSelection = !this.selectedStillMatchesAttempt(st);
    var canContinue = !!(st && st.selected && (st.selected.purchase_ready_reference === true
      || st.selected.private === true && st.selected.review_state === "UNREVIEWED_CONCEPT"));
    if (this.bag) this.bag.disabled = on || this.recoveryBusy || !(st && st.selected && st.selectedReady) || !canContinue
      || !!(st && (st.recoveryPartial || st.unresolvedAttempt || st.recoveryPending || st.savedDesignConflict || st.savedDesignChanged))
      || historicalSelection || selectionNotRetained;
    if (this.save) this.save.disabled = on || this.recoveryBusy || !(st && st.selected && st.selectedReady)
      || !!(st && (st.recoveryPartial || st.unresolvedAttempt || st.recoveryPending || st.savedDesignConflict || st.savedDesignChanged))
      || historicalSelection || selectionNotRetained;
    if (this.asShownButton) this.asShownButton.disabled = on;
    if (this.asShownLink) this.asShownLink.disabled = on;
    if (this.recoveryButton) this.recoveryButton.disabled = on || this.recoveryBusy;
    if (this.resumeButton) this.resumeButton.disabled = on || this.recoveryBusy || this.resumeButton.hidden;
    if (this.finishImagesButton) this.finishImagesButton.disabled = on || this.recoveryBusy || this.finishImagesButton.hidden;
    if (st) this.paintStillActions(st);
    this.syncTake();
  };

  Take.prototype.currentOwnerScope = function (visitor) {
    if (this.cfg.customerId) return "customer:" + String(this.cfg.customerId);
    var visitorId = visitor && visitor.id || readVisitorId();
    return visitorId ? "visitor:" + visitorId : "";
  };

  Take.prototype.currentOwnerScopes = function (visitor) {
    var owners = [];
    if (this.cfg.customerId) owners.push("customer:" + String(this.cfg.customerId));
    var visitorId = visitor && visitor.id || readVisitorId();
    if (visitorId) owners.push("visitor:" + visitorId);
    return owners;
  };

  Take.prototype.pendingGenerationRequest = function (visitor) {
    var helper = root.AuriqGenerationRequests;
    if (!this.door || this.door.kind !== "door" || !helper) return null;
    var owners = this.currentOwnerScopes(visitor);
    return helper.findPendingAcross
      ? helper.findPendingAcross(String(this.door.id), owners)
      : helper.findPending(String(this.door.id), this.currentOwnerScope(visitor));
  };

  Take.prototype.isCurrentRecoveryRequest = function (helper, door, request, owner, visitor) {
    if (this.door !== door || String(door.id) !== request.productId) return false;
    var owners = this.currentOwnerScopes(visitor);
    var pending = helper.findPendingAcross
      ? helper.findPendingAcross(request.productId, owners)
      : helper.findPending(request.productId, owner);
    return !!pending && !pending.blocked_by_other_owner && pending.request_id === request.request_id
      && (!pending.owner || pending.owner === owner);
  };

  Take.prototype.refreshGenerationRecovery = function () {
    if (!this.recoveryPanel) return;
    var helper = root.AuriqGenerationRequests;
    if (!this.door || this.door.kind !== "door" || !helper) {
      this.recoveryPanel.hidden = true;
      return;
    }
    try {
      var owner = this.currentOwnerScope();
      var pending = this.pendingGenerationRequest();
      var st = this.stateFor(this.door);
      var blockedByOtherOwner = !!(pending && pending.blocked_by_other_owner);
      var loadedPilotImage = !!(pending && PILOT_IMAGE_READ_STATES.indexOf(pending.status) !== -1 && st.pilotDraftLoaded);
      st.recoveryPending = blockedByOtherOwner || !!(pending && (pending.status !== "READY_TO_CONTINUE" && !loadedPilotImage
        || (this.cfg.customerId && pending.owner !== owner && !loadedPilotImage) || pending.request_id === st.recoveryPendingRequestId));
      this.recoveryPanel.hidden = !pending;
      var canFinish = !!pending && !blockedByOtherOwner && pending.request_id === st.finishableRequestId;
      if (this.recoveryButton) this.recoveryButton.disabled = this.busy || this.recoveryBusy || !pending || blockedByOtherOwner;
      var canResume = !!pending && !blockedByOtherOwner
        && ["SUPERVISED_INTENT_APPROVED", "OWNER_INTENT_REVIEW_APPROVED"].indexOf(pending.status) !== -1;
      if (this.resumeButton) {
        this.resumeButton.hidden = !canResume;
        this.resumeButton.disabled = this.busy || this.recoveryBusy || !canResume;
      }
      if (this.finishImagesButton) {
        this.finishImagesButton.hidden = !canFinish;
        this.finishImagesButton.disabled = this.busy || this.recoveryBusy || !canFinish;
      }
      this.syncTake();
      if (pending && this.recoveryStatus) {
        var message = blockedByOtherOwner
          ? "A design request needs reconciliation before you can continue. Please contact the shop owner for personal assistance."
          : pending.status === "SUPERVISED_INTENT_REVIEW_REQUIRED" || pending.status === "OWNER_INTENT_REVIEW_REQUIRED"
            ? "Your exact request and choices are saved for manual review. No image call has been made. Check again for a decision."
            : pending.status === "SUPERVISED_INTENT_APPROVED" || pending.status === "OWNER_INTENT_REVIEW_APPROVED"
              ? "Your review is complete. Choose Resume approved request to send this same saved request once."
              : pending.status === "SUPERVISED_INTENT_HELD" || pending.status === "SUPERVISED_INTENT_REVIEW_HELD"
                || pending.status === "OWNER_INTENT_REVIEW_HELD"
                ? "The reviewer has held this request. No image call will be made. Contact the shop owner for personal assistance."
              : pending.status === "SUPERVISED_INTENT_REVIEW_STORAGE_PENDING"
                ? "The supervised intent-review record is still being saved. No image call or replay is available; check status again later."
              : pending.status === "SUPERVISED_REVIEW_UNRESOLVED"
                ? "The saved review evidence could not be verified. No image call, edit or checkout action is authorized; contact the shop owner for personal assistance."
              : PILOT_STATUS_ONLY_STATES.indexOf(pending.status) !== -1
                ? "The supervised review record is still being saved. No retry, edit or checkout action is available; check status again later."
              : LEGACY_PILOT_STATES.indexOf(pending.status) !== -1
                && PILOT_INTENT_STATES.concat(PILOT_IMAGE_STATES).indexOf(pending.status) === -1
                ? "This earlier owner-review record is available for history only. It cannot be replayed, edited or added to checkout."
              : PILOT_IMAGE_READ_STATES.indexOf(pending.status) !== -1
                ? "Your generated image is privately held for manual review. You may edit the selected image; saving to checkout stays unavailable."
          : st.recoveryError || (st.recoveryPartial
            ? "This preview is incomplete. Keep checking this saved request; adding or saving this design is unavailable until recovery completes."
            : pending.owner !== owner && this.cfg.customerId
              ? "A design from this browser is ready to resume. Check the saved request to continue."
              : "Use this control to check the saved request. It will not generate a new image.");
        this.recoveryStatus.textContent = helper.withRequestReference(message, pending);
      }
    } catch (error) {
      if (this.door && this.door.kind === "door") this.stateFor(this.door).recoveryPending = true;
      this.recoveryPanel.hidden = false;
      if (this.recoveryButton) this.recoveryButton.disabled = this.busy || this.recoveryBusy;
      if (this.finishImagesButton) {
        this.finishImagesButton.hidden = true;
        this.finishImagesButton.disabled = true;
      }
      if (this.resumeButton) {
        this.resumeButton.hidden = true;
        this.resumeButton.disabled = true;
      }
      if (this.recoveryStatus) this.recoveryStatus.textContent = "We could not check this browser's saved request state. Please contact the shop owner for personal assistance before starting another image request.";
      this.syncTake();
    }
  };

  function safeShopifyImageUrl(value) {
    if (typeof value !== "string") return false;
    try {
      var url = new URL(value);
      return url.protocol === "https:" &&
        (url.hostname === "cdn.shopify.com" || url.hostname.endsWith(".shopifycdn.com"));
    } catch (error) { return false; }
  }

  function validSavedImageUrl(value) {
    if (!safeShopifyImageUrl(value)) return false;
    try {
      var url = new root.URL(value);
      return !url.username && !url.password && !url.port;
    } catch (error) { return false; }
  }

  function plainStringOptions(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) return false;
    var keys = Object.keys(value);
    return keys.length <= 20 && keys.every(function (key) {
      return key.length > 0 && key.length <= 40 && typeof value[key] === "string" && value[key].length <= 100;
    });
  }

  function parseHelpDesignContext(value) {
    if (typeof value !== "string") throw new Error("Design proposal choices are unavailable.");
    var context;
    try { context = JSON.parse(value); } catch (e) { throw new Error("Design proposal choices are unavailable."); }
    if (!context || typeof context !== "object" || Array.isArray(context)
      || context.stage !== "HELP_CONTEXT_UNVERIFIED"
      || typeof context.productId !== "string" || !PRODUCT_ID_RE.test(context.productId)
      || typeof context.formType !== "string" || typeof context.pair !== "boolean"
      || !plainStringOptions(context.options)) {
      throw new Error("Design proposal choices are unavailable.");
    }
    return context;
  }

  function sameSupportedOptions(intake, proposed) {
    var supported = optionsWithDraft(intake, proposed);
    var keys = Object.keys(proposed);
    var supportedKeys = Object.keys(supported);
    return keys.length === supportedKeys.length && keys.every(function (key) {
      return supported[key] === proposed[key];
    });
  }

  function validSavedDesign(data, requestedId, productId) {
    var design = data && data.design;
    var input = design && design.designInput;
    if (!data || data.success !== true || !design || design.designId !== requestedId
      || !sameProductId(design.productId, productId) || !input || !sameProductId(input.productId, productId)
      || typeof input.formType !== "string" || !input.formType || input.formType.length > 32
      || typeof input.pair !== "boolean" || typeof input.userPrompt !== "string" || input.userPrompt.length > 16384
      || !plainStringOptions(input.options) || !UUID_RE.test(design.trace_id || "")
      || !UUID_RE.test(design.revision_id || "") || !UUID_RE.test(design.attempt_id || "")
      || typeof design.compiledPrompt !== "string" || !design.compiledPrompt || design.compiledPrompt.length > 12000
      || !Array.isArray(design.images) || design.images.length !== 1) return null;
    try {
      var currentUrl = new root.URL(root.location.href);
      var productUrl = new root.URL(design.productUrl, root.location.origin);
      var normalizePath = function (path) { return path.replace(/\/+$/, "") || "/"; };
      if (productUrl.origin !== currentUrl.origin || productUrl.username || productUrl.password
        || normalizePath(productUrl.pathname) !== normalizePath(currentUrl.pathname)) return null;
    } catch (e) {
      return null;
    }
    var image = design.images[0];
    if (!image || !validSavedImageUrl(image.url) || !FILE_GID_RE.test(image.fileId || "")
      || !UUID_RE.test(image.asset_id || "") || image.trace_id !== design.trace_id
      || image.revision_id !== design.revision_id || image.attempt_id !== design.attempt_id) return null;
    return design;
  }

  Take.prototype.showSavedDesignLogin = function () {
    if (!this.status) return;
    this.status.textContent = "Log in to restore this saved design. ";
    var link = document.createElement("a");
    link.href = "/account/login?return_url=" + encodeURIComponent(root.location.pathname + root.location.search);
    link.textContent = "Log in";
    this.status.appendChild(link);
  };

  Take.prototype.refreshSavedDesignCompatibility = function () {
    if (!this.savedDesignHistory || !this.door || this.door.kind !== "door") return;
    var saved = this.savedDesignHistory.designInput;
    var intake = this.door.intake;
    var st = this.stateFor(this.door);
    var conflicts = [];
    if (!intake || saved.formType !== intake.formType) conflicts.push("piece type");
    if (!intake || saved.pair !== !!intake.pair) conflicts.push("piece count or pairing");
    if ((st.motif || "").length > USER_PROMPT_MAX) conflicts.push("motif length");
    var groups = intake ? groupsFor(intake, st.options) : [];
    var currentKeys = Object.create(null);
    groups.forEach(function (group) {
      currentKeys[group.key] = group;
      if (!Object.prototype.hasOwnProperty.call(st.options, group.key)) conflicts.push(group.key);
    });
    Object.keys(st.options).forEach(function (key) {
      var group = currentKeys[key];
      var allowed = intake && intake.clientFields;
      if (allowed && allowed.indexOf(key) === -1) {
        if (!intake.options || intake.options[key] !== st.options[key]) conflicts.push(key);
        return;
      }
      if (!group || !group.items.some(function (item) { return item.value === st.options[key]; })) conflicts.push(key);
    });
    conflicts = conflicts.filter(function (value, index) { return conflicts.indexOf(value) === index; });
    st.savedDesignConflict = conflicts.length > 0;
    var originalKeys = Object.keys(saved.options);
    var currentOptionKeys = Object.keys(st.options);
    st.savedDesignChanged = !intake || saved.formType !== intake.formType || saved.pair !== !!intake.pair
      || st.motif !== saved.userPrompt || originalKeys.length !== currentOptionKeys.length
      || originalKeys.some(function (key) { return st.options[key] !== saved.options[key]; });
    if (this.door) {
      this.setBusy(this.busy);
      if (st.savedDesignConflict) {
        this.setStatus("This saved design has choices that no longer fit the current setup (" + conflicts.join(", ") + "). Adjust the available choices or contact the shop owner for personal assistance before taking a new image.");
      } else if (st.savedDesignChanged) {
        this.setStatus("Your choices have changed since this image was saved. Take a new image to apply the changes before saving or adding it to your bag.");
      } else {
        this.setStatus("Your saved design details and selected image are restored. Review the choices before continuing.");
      }
    }
  };

  Take.prototype.tryResumeSavedDesign = function (retry) {
    var self = this;
    if (!this.savedDesignId || !this.sheet || this.savedDesignResumeStarted || this.savedDesignResumeBusy || !this.configResolved) return;
    var door = this.door;
    if (!door || door.kind !== "door" || !door.id || !this.cfg.appUrl) {
      this.savedDesignResumeStarted = true;
      this.setStatus("This saved design could not be restored here. Open its product page or contact the shop owner for personal assistance.");
      return;
    }
    if (!this.cfg.customerId) {
      this.savedDesignResumeStarted = true;
      this.showSavedDesignLogin();
      return;
    }
    var helper = root.AuriqGenerationRequests;
    var resumeOwner = "";
    var resumePending = null;
    if (!helper || typeof helper.findPending !== "function") {
      if (this.savedDesignRetryButton) this.savedDesignRetryButton.hidden = false;
      this.setStatus("A saved image request could not be checked. Recover it before restoring this design.");
      return;
    }
    try {
      resumeOwner = this.currentOwnerScope();
      resumePending = this.pendingGenerationRequest();
      if (resumePending && (resumePending.status !== "READY_TO_CONTINUE" || resumePending.owner !== resumeOwner)) {
        this.savedDesignResumeWaitingForRecovery = true;
        this.setStatus("Recover the earlier image request first. After it is resolved, this saved design will resume.");
        return;
      }
    } catch (e) {
      if (this.savedDesignRetryButton) this.savedDesignRetryButton.hidden = false;
      this.setStatus("A saved image request could not be checked. Recover it before restoring this design.");
      return;
    }
    this.savedDesignResumeStarted = true;
    this.savedDesignResumeBusy = true;
    this.savedDesignResumeWaitingForRecovery = false;
    if (this.savedDesignRetryButton) this.savedDesignRetryButton.hidden = true;
    if (retry) this.setStatus("Restoring your saved design…");
    this.setBusy(true);
    root.fetch(this.cfg.appUrl + "/api/saved-design", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ designId: this.savedDesignId })
    }).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (data) {
        return { response: response, data: data };
      });
    }).then(function (result) {
      if (result.response.status === 401) {
        self.showSavedDesignLogin();
        return;
      }
      var design = result.response.ok ? validSavedDesign(result.data, self.savedDesignId, door.id) : null;
      if (!design) throw new Error("unavailable");
      var currentPending = self.pendingGenerationRequest();
      if ((resumePending && (!currentPending || currentPending.request_id !== resumePending.request_id
        || currentPending.owner !== resumeOwner || currentPending.status !== "READY_TO_CONTINUE"
        || currentPending.trace_id !== resumePending.trace_id || currentPending.attempt_id !== resumePending.attempt_id
        || currentPending.selected_asset_id !== resumePending.selected_asset_id))
        || (!resumePending && currentPending)) {
        throw new Error("request state changed");
      }
      var input = design.designInput;
      var st = self.stateFor(door);
      self.savedDesignHistory = design;
      st.options = copy(input.options);
      st.motif = input.userPrompt;
      st.compiledPrompt = design.compiledPrompt;
      st.designInput = input;
      st.designSummary = design.designSummary;
      st.traceId = design.trace_id;
      st.revisionId = design.revision_id;
      st.attemptId = design.attempt_id;
      st.stills = design.images.map(function (image) {
        return {
          url: image.url,
          fileId: image.fileId,
          asset_id: image.asset_id,
          trace_id: image.trace_id,
          revision_id: image.revision_id,
          attempt_id: image.attempt_id,
          designInput: copy(input),
          designSummary: design.designSummary || null,
          compiledPrompt: design.compiledPrompt || "",
          owner_scope: resumeOwner,
          request_id: ""
        };
      });
      st.selected = st.stills[0];
      st.lastFailureTraceId = "";
      st.lastFailureAttemptId = "";
      st.unresolvedAttempt = false;
      st.recoveryPartial = false;
      if (self.door === door) {
        if (self.motif) self.motif.value = input.userPrompt;
        self.renderFields();
        self.paintStill();
        self.paintAsShown();
        self.paintChrome();
        self.revealCanvas();
      }
      self.savedDesignResumeStarted = true;
      self.savedDesignResumeWaitingForRecovery = false;
      if (self.savedDesignRetryButton) self.savedDesignRetryButton.hidden = true;
    }).catch(function () {
      self.savedDesignResumeStarted = false;
      if (self.savedDesignRetryButton) self.savedDesignRetryButton.hidden = false;
      self.setStatus("This saved design is unavailable right now. You can try again or contact the shop owner for personal assistance.");
    }).finally(function () {
      self.savedDesignResumeBusy = false;
      self.setBusy(false);
      self.refreshGenerationRecovery();
    });
  }

  Take.prototype.claimSelectedStill = async function (door, still) {
    var helper = root.AuriqGenerationRequests;
    if (!this.cfg.customerId || !helper) return;
    try {
      await helper.claimSelection(this.cfg.appUrl, String(door.id), still, this.currentOwnerScope(), readVisitor());
    } catch {
      throw new Error("This guest design could not be verified for your account. Please contact the shop owner for personal assistance.");
    }
  };

  Take.prototype.recoverGeneration = function () {
    return this.reconcileGeneration(false);
  };

  Take.prototype.finishSavedImages = function () {
    return this.reconcileGeneration(true);
  };

  Take.prototype.loadPilotDraftImages = async function (data, request, owner, visitor, st, door, readOnly) {
    var helper = root.AuriqGenerationRequests;
    var payload = helper && helper.savedPayload(String(door.id), request.request_id, owner);
    var input = payload && payload.body;
    var imageState = data && data.pilot_review_state;
    var editableStates = ["SUPERVISED_IMAGE_REVIEWED", "SUPERVISED_IMAGE_HELD", "SUPERVISED_IMAGE_UNRESOLVED",
      "OWNER_IMAGE_REVIEWED", "OWNER_IMAGE_HELD", "OWNER_IMAGE_UNRESOLVED"];
    var canEdit = !readOnly && data.can_edit_draft === true;
    if (!payload || !input || String(input.productId) !== String(door.id) || !door.intake
      || input.formType !== door.intake.formType || !!input.pair !== !!door.intake.pair
      || typeof input.userPrompt !== "string" || !plainStringOptions(input.options)
      || !(readOnly ? LEGACY_PILOT_IMAGE_STATES.includes(imageState) : PILOT_IMAGE_STATES.includes(imageState))
      || data.recovery_state !== "MANUAL_REVIEW"
      || data.state !== imageState || data.manual_review_required !== true
      || data.can_edit_draft !== (!readOnly && editableStates.includes(imageState)) || canEdit && !editableStates.includes(imageState)
      || data.can_resume_same_request !== false || data.purchase_ready_reference !== false
      || !Array.isArray(data.draft_images) || !data.draft_images.length
      || !UUID_RE.test(data.trace_id || "") || !UUID_RE.test(data.revision_id || "")
      || !UUID_RE.test(data.attempt_id || "") || (request.trace_id && request.trace_id !== data.trace_id)
      || (request.attempt_id && request.attempt_id !== data.attempt_id)) {
      throw new Error("The saved image review could not be verified. Keep its reference and contact the shop owner for personal assistance.");
    }
    var seenAssets = Object.create(null);
    var images = data.draft_images.map(function (image) {
      var reviewStateMatches = readOnly
        ? LEGACY_PILOT_IMAGE_STATES.includes(image && image.review_state) || image && image.review_state === "UNRESOLVED"
        : image && (image.review_state === imageState
          || imageState === "OWNER_IMAGE_REVIEWED" && ["OWNER_IMAGE_HELD", "OWNER_IMAGE_UNRESOLVED"].includes(image.review_state));
      if (!image || typeof image !== "object" || Array.isArray(image)
        || image.private !== true || image.purchase_ready_reference !== false
        || image.role !== "PUBLIC_DERIVATIVE" || image.mime_type !== "image/png"
        || !reviewStateMatches || !/^[a-f0-9]{64}$/i.test(image.sha256 || "")
        || image.url !== undefined || !UUID_RE.test(image.asset_id || "")
        || image.trace_id !== data.trace_id || image.revision_id !== data.revision_id
        || image.attempt_id !== data.attempt_id || !Number.isInteger(image.candidate_ordinal)
        || image.candidate_ordinal < 0 || seenAssets[image.asset_id]) {
        throw new Error("The saved image review could not be verified. Keep its reference and contact the shop owner for personal assistance.");
      }
      seenAssets[image.asset_id] = true;
      return image;
    });
    var objectUrls = [];
    try {
      var stills = await Promise.all(images.map(async function (image) {
        var requestBody = {
          request_id: request.request_id, productId: String(door.id),
          trace_id: image.trace_id, asset_id: image.asset_id
        };
        if (owner.indexOf("visitor:") === 0) {
          if (!visitor || owner !== "visitor:" + visitor.id || !visitor.hmac) {
            throw new Error("The original browser session could not be verified. Reopen this design there or contact the shop owner for personal assistance.");
          }
          requestBody.visitor_id = visitor.id;
          requestBody.visitor_hmac = visitor.hmac;
        }
        var response = await root.fetch(this.cfg.appUrl + "/api/design-pilot-asset", {
          method: "POST", credentials: "same-origin", cache: "no-store",
          headers: { "Content-Type": "application/json", Accept: "image/png" },
          body: JSON.stringify(requestBody)
        });
        if (!response.ok || (response.headers.get("Content-Type") || "").split(";")[0].trim() !== "image/png") {
          throw new Error("The saved private image is unavailable.");
        }
        var blob = await response.blob();
        var objectUrl = root.URL.createObjectURL(blob);
        objectUrls.push(objectUrl);
        return {
          url: objectUrl, fileId: "", trace_id: image.trace_id, revision_id: image.revision_id,
          attempt_id: image.attempt_id, asset_id: image.asset_id, review_state: image.review_state,
          purchase_ready_reference: false, owner_scope: owner, request_id: request.request_id,
          candidate_ordinal: image.candidate_ordinal
        };
      }, this));
      if (this.door !== door || !this.isCurrentRecoveryRequest(helper, door, request, owner, visitor)) {
        objectUrls.forEach(function (url) { root.URL.revokeObjectURL(url); });
        return false;
      }
      (st.pilotObjectUrls || []).forEach(function (url) { root.URL.revokeObjectURL(url); });
      st.pilotObjectUrls = objectUrls;
      st.options = copy(input.options);
      st.motif = input.userPrompt;
      st.designInput = {
        productId: String(door.id), formType: input.formType, pair: !!input.pair,
        options: copy(input.options), userPrompt: input.userPrompt
      };
      st.traceId = data.trace_id;
      st.revisionId = data.revision_id;
      st.attemptId = data.attempt_id;
      st.lastFailureTraceId = "";
      st.lastFailureAttemptId = "";
      st.unresolvedAttempt = false;
      st.recoveryPartial = false;
      st.recoveryPending = !!readOnly || !canEdit;
      st.recoveryPendingRequestId = readOnly || !canEdit ? request.request_id : "";
      st.pilotReviewState = imageState;
      st.pilotDraftLoaded = true;
      st.pilotDraftEditable = canEdit;
      st.stills = stills;
      var selectedAssetId = request.selected_asset_id || "";
      var selectedIndex = selectedAssetId ? stills.findIndex(function (still) { return still.asset_id === selectedAssetId; }) : 0;
      if (selectedIndex < 0) throw new Error("The saved image selection is unavailable.");
      st.selected = stills[selectedIndex];
      helper.update(String(door.id), request.request_id, owner, imageState, {
        trace_id: data.trace_id, attempt_id: data.attempt_id
      });
      try {
        st.selected.selection_persisted = !!helper.recordSelection(String(door.id), request.request_id, owner,
          st.selected.asset_id, { trace_id: st.selected.trace_id, attempt_id: st.selected.attempt_id });
      } catch (error) { st.selected.selection_persisted = false; }
      if (this.door === door) {
        if (this.motif) this.motif.value = input.userPrompt;
        if (this.count) this.count.textContent = String(input.userPrompt.length);
        this.renderFields();
        this.paintStill();
        this.revealCanvas();
        this.setStatus(readOnly
          ? "This earlier owner-review image is available privately for history only. It cannot be replayed, edited or added to checkout."
          : canEdit
            ? "This private image remains held from purchase. You can make a selected-image edit; checkout is unavailable."
            : "This private draft is available to review, but editing and checkout remain unavailable until its image review is recorded.");
      }
      return true;
    } catch (error) {
      objectUrls.forEach(function (url) { root.URL.revokeObjectURL(url); });
      throw error;
    }
  };

  Take.prototype.showLegacyPilotReviewState = async function (data, request, owner, visitor, st, door) {
    var helper = root.AuriqGenerationRequests;
    var payload = helper && helper.savedPayload(String(door.id), request.request_id, owner);
    var input = payload && payload.body;
    var state = data && data.pilot_review_state;
    if (!payload || !input || String(input.productId) !== String(door.id) || !door.intake
      || input.formType !== door.intake.formType || !!input.pair !== !!door.intake.pair
      || typeof input.userPrompt !== "string" || !plainStringOptions(input.options)
      || LEGACY_PILOT_STATES.indexOf(state) === -1 || data.recovery_state !== "MANUAL_REVIEW"
      || data.state !== state || data.manual_review_required !== true || data.purchase_ready_reference !== false
      || data.can_edit_draft !== false || data.can_resume_same_request !== false
      || !Array.isArray(data.draft_images)
      || !UUID_RE.test(data.trace_id || "") || !UUID_RE.test(data.revision_id || "")
      || !UUID_RE.test(data.attempt_id || "") || (request.trace_id && request.trace_id !== data.trace_id)
      || (request.attempt_id && request.attempt_id !== data.attempt_id)) {
      throw new Error("This earlier review record could not be verified. It remains read-only; contact the shop owner for personal assistance.");
    }
    if (data.draft_images.length) {
      if (LEGACY_PILOT_IMAGE_STATES.indexOf(state) === -1) {
      throw new Error("The earlier review source is inconsistent. It remains read-only; contact the shop owner for personal assistance.");
      }
      return this.loadPilotDraftImages(data, request, owner, visitor, st, door, true);
    }
    if (!helper.update(String(door.id), request.request_id, owner, state, {
      trace_id: data.trace_id, attempt_id: data.attempt_id
    })) throw new Error("The saved request changed. Check its status again.");
    st.pilotReviewState = state;
    st.pilotDraftLoaded = false;
    st.pilotDraftEditable = false;
    st.traceId = data.trace_id;
    st.revisionId = data.revision_id;
    st.attemptId = data.attempt_id;
    st.recoveryPending = true;
    st.recoveryPendingRequestId = request.request_id;
    if (this.door === door) {
      st.options = copy(input.options);
      st.motif = input.userPrompt;
      st.designInput = { productId: String(door.id), formType: input.formType, pair: !!input.pair,
        options: copy(input.options), userPrompt: input.userPrompt };
      if (this.motif) this.motif.value = input.userPrompt;
      if (this.count) this.count.textContent = String(input.userPrompt.length);
      this.renderFields();
      this.setStatus("This earlier owner-review record is retained for history only. It cannot be replayed, edited or added to checkout.");
    }
    return true;
  };

  Take.prototype.showPilotStatusOnlyState = function (data, request, owner, st, door) {
    var helper = root.AuriqGenerationRequests;
    var payload = helper && helper.savedPayload(String(door.id), request.request_id, owner);
    var input = payload && payload.body;
    var state = data && data.pilot_review_state;
    if (!payload || !input || String(input.productId) !== String(door.id) || !door.intake
      || input.formType !== door.intake.formType || !!input.pair !== !!door.intake.pair
      || typeof input.userPrompt !== "string" || !plainStringOptions(input.options)
      || PILOT_STATUS_ONLY_STATES.indexOf(state) === -1 || data.recovery_state !== "MANUAL_REVIEW"
      || data.state !== state || data.manual_review_required !== true || data.purchase_ready_reference !== false
      || data.can_edit_draft !== false || data.can_resume_same_request !== false
      || !Array.isArray(data.draft_images) || data.draft_images.length !== 0
      || !UUID_RE.test(data.trace_id || "") || !UUID_RE.test(data.revision_id || "")
      || !UUID_RE.test(data.attempt_id || "") || (request.trace_id && request.trace_id !== data.trace_id)
      || (request.attempt_id && request.attempt_id !== data.attempt_id)) {
      throw new Error("The supervised review record is still unresolved. No action is authorized; contact the shop owner for personal assistance.");
    }
    if (!helper.update(String(door.id), request.request_id, owner, state, {
      trace_id: data.trace_id, attempt_id: data.attempt_id
    })) throw new Error("The saved request changed. Check its status again.");
    st.pilotReviewState = state;
    st.pilotDraftLoaded = false;
    st.pilotDraftEditable = false;
    st.traceId = data.trace_id;
    st.revisionId = data.revision_id;
    st.attemptId = data.attempt_id;
    st.recoveryPending = true;
    st.recoveryPendingRequestId = request.request_id;
    if (this.door === door) this.setStatus("The supervised review record is still being saved. No retry, edit or checkout action is available; check status again later.");
    return true;
  };

  Take.prototype.showPilotReviewState = function (data, request, owner, visitor, st, door) {
    var helper = root.AuriqGenerationRequests;
    var payload = helper && helper.savedPayload(String(door.id), request.request_id, owner);
    var input = payload && payload.body;
    var state = data && data.pilot_review_state;
    var canResume = state === "SUPERVISED_INTENT_APPROVED" || state === "OWNER_INTENT_REVIEW_APPROVED";
    if (!payload || !input || String(input.productId) !== String(door.id) || !door.intake
      || input.formType !== door.intake.formType || !!input.pair !== !!door.intake.pair
      || typeof input.userPrompt !== "string" || !plainStringOptions(input.options)
      || !PILOT_INTENT_STATES.includes(state) || data.recovery_state !== "MANUAL_REVIEW"
      || data.state !== state || data.manual_review_required !== true || data.purchase_ready_reference !== false
      || data.can_edit_draft !== false
      || data.can_resume_same_request !== canResume
      || !Array.isArray(data.draft_images)
      || data.draft_images.length !== 0
      || !UUID_RE.test(data.trace_id || "") || !UUID_RE.test(data.revision_id || "")
      || !UUID_RE.test(data.attempt_id || "") || (request.trace_id && request.trace_id !== data.trace_id)
      || (request.attempt_id && request.attempt_id !== data.attempt_id)) {
      throw new Error("The saved request review could not be verified. Keep its reference and contact the shop owner for personal assistance.");
    }
    if (!helper.update(String(door.id), request.request_id, owner, state, {
      trace_id: data.trace_id, attempt_id: data.attempt_id
    })) throw new Error("The saved request changed. Check its status again.");
    st.pilotReviewState = state;
    st.pilotDraftLoaded = false;
    st.pilotDraftEditable = false;
    st.traceId = data.trace_id;
    st.revisionId = data.revision_id;
    st.attemptId = data.attempt_id;
    st.recoveryPending = true;
    st.recoveryPendingRequestId = "";
    if (this.door === door) {
      st.options = copy(input.options);
      st.motif = input.userPrompt;
      st.designInput = { productId: String(door.id), formType: input.formType, pair: !!input.pair,
        options: copy(input.options), userPrompt: input.userPrompt };
      if (this.motif) this.motif.value = input.userPrompt;
      if (this.count) this.count.textContent = String(input.userPrompt.length);
      this.renderFields();
      var reviewMessage = state === "SUPERVISED_INTENT_APPROVED" || state === "OWNER_INTENT_REVIEW_APPROVED"
        ? "Your review is complete. Choose Resume approved request to send this exact saved request once."
        : state === "OWNER_INTENT_REVIEW_REQUIRED"
          ? "Your exact request and choices are saved for owner review. No image call has been made. Check again for a decision."
        : state === "SUPERVISED_INTENT_HELD" || state === "SUPERVISED_INTENT_REVIEW_HELD"
          ? "The reviewer has held this request. No image call will be made. Contact the shop owner for personal assistance."
        : state === "OWNER_INTENT_REVIEW_HELD"
          ? "The shop owner has held this request. No image call will be made. Contact the shop owner for personal assistance."
          : state === "SUPERVISED_INTENT_REVIEW_STORAGE_PENDING"
            ? "The supervised intent-review record is still being saved. No image call or replay is available; check status again later."
            : state === "SUPERVISED_REVIEW_UNRESOLVED"
              ? "The saved review evidence could not be verified. No image call, edit or checkout action is authorized; contact the shop owner for personal assistance."
          : "Your exact request and choices are saved for supervised manual review. No image call has been made.";
      this.setStatus(reviewMessage);
    }
  };

  Take.prototype.resumeApprovedGeneration = async function () {
    var door = this.door;
    var helper = root.AuriqGenerationRequests;
    if (this.busy || this.recoveryBusy || !door || door.kind !== "door" || !helper || !this.cfg.appUrl) return;
    var pending = this.pendingGenerationRequest();
    var approvedStates = ["SUPERVISED_INTENT_APPROVED", "OWNER_INTENT_REVIEW_APPROVED"];
    if (!pending || pending.blocked_by_other_owner || approvedStates.indexOf(pending.status) === -1) return;
    var request = helper.recoveryRequest(String(door.id), pending.request_id, pending.owner);
    if (!request || !helper.approvedPayload(String(door.id), request.request_id, pending.owner)) return;
    var visitor = null;
    var st = this.stateFor(door);
    this.recoveryBusy = true;
    this.setBusy(true);
    this.setStatus("Sending the same approved request…");
    try {
      if (pending.owner.indexOf("visitor:") === 0) {
        visitor = await ensureVisitorForExplicitAction();
        if (!visitor || pending.owner !== "visitor:" + visitor.id) throw new Error("The original browser session could not be verified.");
      }
      var customerOwner = this.cfg.customerId ? this.currentOwnerScope() : "";
      var decision = await helper.requestRecovery(this.cfg.appUrl, request, pending.owner, customerOwner, visitor);
      if (!this.isCurrentRecoveryRequest(helper, door, request, pending.owner, visitor)) return;
      var decisionData = decision.data;
      var decisionState = decisionData && decisionData.pilot_review_state;
      if (!decision.response.ok || decisionData.request_id !== request.request_id
        || decisionData.recovery_state !== "MANUAL_REVIEW" || decisionData.state !== decisionState
        || decisionData.manual_review_required !== true
        || (PILOT_INTENT_STATES.indexOf(decisionState) === -1 && LEGACY_PILOT_STATES.indexOf(decisionState) === -1)
        || !UUID_RE.test(decisionData.trace_id || "") || !UUID_RE.test(decisionData.revision_id || "")
        || !UUID_RE.test(decisionData.attempt_id || "")
        || (request.trace_id && request.trace_id !== decisionData.trace_id)
        || (request.attempt_id && request.attempt_id !== decisionData.attempt_id)) {
        throw new Error("The approved request could not be freshly verified. No image request was sent.");
      }
      var canResume = decisionState === "SUPERVISED_INTENT_APPROVED" || decisionState === "OWNER_INTENT_REVIEW_APPROVED";
      if (decisionData.can_resume_same_request !== canResume || decisionState !== pending.status || !canResume) {
        if (PILOT_INTENT_STATES.indexOf(decisionState) !== -1) {
          this.showPilotReviewState(decisionData, request, pending.owner, visitor, st, door);
        } else {
          this.showLegacyPilotReviewState(decisionData, request, pending.owner, visitor, st, door);
        }
        return;
      }
      var result = await helper.resumeApproved(this.cfg.appUrl, String(door.id), request.request_id, pending.owner, visitor);
      if (!this.isCurrentRecoveryRequest(helper, door, request, pending.owner, visitor)) return;
      var data = result.data;
      if (data.dispatch_state === "SUPERVISED_INTENT_REVIEW_REQUIRED") {
        this.showPilotReviewState(Object.assign({}, data, { recovery_state: "MANUAL_REVIEW", pilot_review_state: data.state, draft_images: [] }),
          request, pending.owner, visitor, st, door);
      } else if (PILOT_IMAGE_STATES.includes(data.state)) {
        helper.update(String(door.id), request.request_id, pending.owner, data.state, {
          trace_id: data.trace_id, attempt_id: data.attempt_id
        });
        st.pilotReviewState = data.state;
        st.traceId = data.trace_id;
        st.revisionId = data.revision_id;
        st.attemptId = data.attempt_id;
        await this.loadPilotDraftImages(Object.assign({}, data, { recovery_state: "MANUAL_REVIEW", pilot_review_state: data.state }),
          request, pending.owner, visitor, st, door);
      } else {
        throw new Error("The approved request response needs reconciliation before you continue.");
      }
    } catch (error) {
      st.recoveryError = error && error.message ? error.message : "The approved request could not be confirmed.";
      if (this.door === door) this.setStatus(st.recoveryError + " No replacement request was sent.");
    } finally {
      this.recoveryBusy = false;
      this.setBusy(false);
      this.refreshGenerationRecovery();
    }
  };

  Take.prototype.reconcileGeneration = async function (finishImages) {
    var door = this.door;
    var helper = root.AuriqGenerationRequests;
    if (this.busy || this.recoveryBusy || !door || door.kind !== "door" || !helper || !this.cfg.appUrl) return;
    var st = this.stateFor(door);
    var recoveryRequestId = "";
    var recoveryRequestOwner = "";
    var visitor = null;
    st.recoveryError = "";
    this.recoveryBusy = true;
    this.refreshGenerationRecovery();
    if (this.recoveryStatus) this.recoveryStatus.textContent = finishImages ? "Finishing saved images…" : "Checking the saved request…";
    try {
      visitor = this.cfg.customerId ? readVisitor() : await ensureVisitorForExplicitAction();
      if (!this.cfg.customerId && (!visitor || !visitor.id || !visitor.hmac)) throw new Error("session");
      var pending = this.pendingGenerationRequest(visitor);
      if (pending && pending.blocked_by_other_owner) throw new Error("foreign");
      var sourceOwner = pending && pending.owner || this.currentOwnerScope(visitor);
      var request = pending && helper.recoveryRequest(String(door.id), pending.request_id, sourceOwner);
      if (!request) throw new Error("No saved request is available for this design.");
      if (finishImages && st.finishableRequestId !== request.request_id) throw new Error("missing");
      recoveryRequestId = request.request_id;
      recoveryRequestOwner = sourceOwner;
      var customerOwner = this.cfg.customerId ? this.currentOwnerScope() : "";
      var result = finishImages
        ? await helper.completionRequest(this.cfg.appUrl, request, sourceOwner, customerOwner, visitor)
        : await helper.requestRecovery(this.cfg.appUrl, request, sourceOwner, customerOwner, visitor);

      var data = result.data;
      if (data.request_id !== request.request_id
        || !this.isCurrentRecoveryRequest(helper, door, request, sourceOwner, visitor)) return;
      if (result.response.ok && data.success === true
        && data.state === "CONCEPT_PREVIEW" && data.recovery_state === "SUCCEEDED") {
        var conceptSettlement = await helper.settleRecovery(this.cfg.appUrl, String(door.id), request, sourceOwner,
          customerOwner, visitor, result);
        if (!this.isCurrentRecoveryRequest(helper, door, request, conceptSettlement.owner, visitor)) return;
        var recoveredImages = await this.loadConceptPreviewImages(data, request, conceptSettlement.owner, visitor, door);
        if (this.door !== door || !this.isCurrentRecoveryRequest(helper, door, request, conceptSettlement.owner, visitor)) {
          recoveredImages.forEach(function (image) { root.URL.revokeObjectURL(image.url); });
          return;
        }
        var priorSelection = st.selected;
        st.pilotObjectUrls = (st.pilotObjectUrls || []).concat(recoveredImages.map(function (image) { return image.url; }));
        st.stills = (st.stills || []).concat(recoveredImages);
        st.recoveryPendingRequestId = "";
        st.recoveryPending = false;
        st.recoveryPartial = false;
        st.unresolvedAttempt = false;
        st.recoveryError = "";
        st.finishableRequestId = "";
        st.options = copy(data.designInput.options);
        st.draftOptions = copy(data.designInput.options);
        st.motif = data.designInput.userPrompt;
        st.traceId = data.trace_id;
        st.revisionId = data.revision_id;
        st.attemptId = data.attempt_id;
        st.designInput = data.designInput;
        st.designSummary = data.designSummary || null;
        st.compiledPrompt = typeof data.compiledPrompt === "string" ? data.compiledPrompt : "";
        if (!priorSelection) {
          st.selected = conceptSettlement.selected_asset_id
            ? recoveredImages.find(function (image) { return image.asset_id === conceptSettlement.selected_asset_id; }) || recoveredImages[0]
            : recoveredImages[0];
          try {
            st.selected.selection_persisted = !!helper.recordSelection(String(door.id), request.request_id,
              conceptSettlement.owner, st.selected.asset_id, { trace_id: st.selected.trace_id, attempt_id: st.selected.attempt_id });
          } catch (error) { st.selected.selection_persisted = false; }
        }
        if (this.motif) this.motif.value = data.designInput.userPrompt;
        if (this.count) this.count.textContent = String(data.designInput.userPrompt.length);
        this.renderFields();
        this.paintStill();
        this.setStatus(priorSelection
          ? "Your saved concept preview is in the gallery. Your selected reference is unchanged."
          : "Your saved concept preview has been restored. Final materials, dimensions and construction are confirmed with the owner.");
        this.revealCanvas();
        this.refreshGenerationRecovery();
        return;
      }
      if (!finishImages && data.recovery_state === "MANUAL_REVIEW") {
        if (PILOT_IMAGE_STATES.indexOf(data.pilot_review_state) !== -1) {
          await this.loadPilotDraftImages(data, request, sourceOwner, visitor, st, door);
        } else if (PILOT_INTENT_STATES.indexOf(data.pilot_review_state) !== -1) {
          this.showPilotReviewState(data, request, sourceOwner, visitor, st, door);
        } else if (LEGACY_PILOT_STATES.indexOf(data.pilot_review_state) !== -1) {
          await this.showLegacyPilotReviewState(data, request, sourceOwner, visitor, st, door);
        } else if (PILOT_STATUS_ONLY_STATES.indexOf(data.pilot_review_state) !== -1) {
          this.showPilotStatusOnlyState(data, request, sourceOwner, st, door);
        } else {
          throw new Error("The saved review state is unavailable. Keep its reference and contact the shop owner for personal assistance.");
        }
        this.refreshGenerationRecovery();
        return;
      }
      if (data.can_finish_saved_images !== true) st.finishableRequestId = "";
      if (data.success === false && data.recovery_state === "PENDING" && data.can_generate_revision === false) {
        st.finishableRequestId = data.can_finish_saved_images === true ? request.request_id : "";
        st.recoveryPendingRequestId = request.request_id;
        st.recoveryError = data.pilot_review_state === "OWNER_IMAGE_REVIEWED" && data.can_finish_saved_images === true
          ? "Owner-reviewed images are ready to finish from their saved files. No new image request will be sent."
          : "We could not confirm whether the saved request finished. Keep its reference and contact the shop owner for personal assistance before starting another image request.";
        this.refreshGenerationRecovery();
        return;
      }
      if (!result.response.ok) {
        st.finishableRequestId = "";
        throw new Error("unresolved");
      }
      var partial = data.recovery_state === "PARTIAL";
      var completeState = data.recovery_state === "SUCCEEDED" || data.recovery_state === "FAILED_SETTLED";
      var input = data.designInput;
      if (!input || String(input.productId) !== String(door.id) || !door.intake ||
        input.formType !== door.intake.formType || !!input.pair !== !!door.intake.pair ||
        typeof input.userPrompt !== "string" || !plainStringOptions(input.options) ||
        !UUID_RE.test(data.trace_id || "") || !UUID_RE.test(data.revision_id || "") || !UUID_RE.test(data.attempt_id || "") ||
        typeof data.can_finish_saved_images !== "boolean") {
        throw new Error("We could not confirm whether the saved request belongs to this design. Keep its reference and contact the shop owner for personal assistance.");
      }
      if (data.recovery_state === "SUCCEEDED" || partial) {
        if (!Array.isArray(data.images) || !data.images.length || data.images.some(function (image) {
          return !image || !safeShopifyImageUrl(image.url) || !UUID_RE.test(image.asset_id || "");
        })) throw new Error("We could not verify the saved images. Keep the request reference and contact the shop owner for personal assistance.");
        if ((partial && (!Array.isArray(data.candidate_failures) || !data.candidate_failures.length)) ||
          (!partial && (!Array.isArray(data.candidate_failures) || data.candidate_failures.length))) {
          throw new Error("We could not verify the saved request state. Keep the request reference and contact the shop owner for personal assistance.");
        }
      } else if (!Array.isArray(data.images) || data.images.length) {
        throw new Error("The saved request could not be reconciled safely.");
      }
      st.finishableRequestId = partial && data.can_finish_saved_images ? request.request_id : "";
      var previousSelectedAssetId = st.selected && st.selected.asset_id || "";

      var settlement = await helper.settleRecovery(this.cfg.appUrl, String(door.id), request, sourceOwner,
        customerOwner, visitor, result);
      if (!this.isCurrentRecoveryRequest(helper, door, request, settlement.owner, visitor)) return;
      st.recoveryPendingRequestId = "";
      st.recoveryPending = false;
      var resumedOwner = settlement.owner;
      var selectedAssetId = settlement.selected_asset_id;

      st.recoveryError = "";
      st.options = copy(input.options);
      st.motif = input.userPrompt;
      st.compiledPrompt = typeof data.compiledPrompt === "string" ? data.compiledPrompt : "";
      st.designInput = input;
      st.designSummary = data.designSummary;
      st.traceId = data.trace_id;
      st.revisionId = data.revision_id;
      st.attemptId = data.attempt_id;
      if (this.door === door) {
        if (this.motif) this.motif.value = input.userPrompt;
        if (this.count) this.count.textContent = String(input.userPrompt.length);
        this.renderFields();
        this.syncTake();
        if (this.savedDesignHistory) this.refreshSavedDesignCompatibility();
      }

      if (data.recovery_state === "SUCCEEDED" || partial) {
        st.stills = data.images.map(function (image) {
          return {
            url: image.url,
            fileId: image.fileId || "",
            trace_id: image.trace_id || data.trace_id,
            revision_id: image.revision_id || data.revision_id,
            attempt_id: image.attempt_id || data.attempt_id,
            asset_id: image.asset_id,
            review_state: image.review_state,
            purchase_ready_reference: image.purchase_ready_reference,
            owner_scope: resumedOwner,
            request_id: request.request_id
          };
        });
        if (partial && previousSelectedAssetId && st.stills.some(function (still) { return still.asset_id === previousSelectedAssetId; })) {
          selectedAssetId = previousSelectedAssetId;
        }
        var selectedIndex = selectedAssetId
          ? st.stills.findIndex(function (still) { return still.asset_id === selectedAssetId; })
          : 0;
        if (selectedIndex < 0) throw new Error("The saved image selection could not be restored safely.");
        st.selected = st.stills[selectedIndex];
        st.lastFailureTraceId = "";
        st.lastFailureAttemptId = "";
        st.recoveryPartial = partial;
        st.unresolvedAttempt = partial;
        if (!partial) {
          try {
            st.selected.selection_persisted = !!helper.recordSelection(
              String(door.id), request.request_id, resumedOwner, st.selected.asset_id, {
                trace_id: st.selected.trace_id,
                attempt_id: st.selected.attempt_id
              }
            );
          } catch (error) {
            st.selected.selection_persisted = false;
          }
          if (this.helpProposalStatus === "ACCEPTED") this.completeAcceptedDesignHelpProposal(st);
        }
        if (this.door === door) {
          this.paintStill();
          this.paintAsShown();
          this.setBusy(false);
          if (!partial && this.take) this.take.textContent = this.take.getAttribute("data-label-again") || "Take again";
          this.setStatus(partial
            ? "This image preview is incomplete while the remaining files are prepared. You cannot save or add it to your bag until the saved request is fully recovered."
            : st.selected.selection_persisted === false
              ? "We could not retain this image selection for recovery. Please try again."
              : "Your saved design request has been restored.");
          this.revealCanvas();
        }
      } else {
        st.recoveryPartial = false;
        st.lastFailureTraceId = data.trace_id;
        st.lastFailureAttemptId = data.attempt_id;
        st.unresolvedAttempt = false;
        if (this.door === door) {
          this.paintStillActions(st);
          if (this.take) this.take.textContent = this.take.getAttribute("data-label-again") || "Take again";
          this.setStatus(this.selectedStillMatchesAttempt(st)
            ? "The saved request finished without an image. Your current preview and saved design remain in place."
            : "The saved request finished without an image. Your earlier preview remains visible; take a new image before saving or ordering it.");
        }
      }
      this.refreshGenerationRecovery();
      if (this.savedDesignResumeWaitingForRecovery && completeState) {
        this.savedDesignResumeWaitingForRecovery = false;
        this.tryResumeSavedDesign(true);
      }
    } catch (error) {
      if (finishImages && recoveryRequestId
        && this.isCurrentRecoveryRequest(helper, door,
          { request_id: recoveryRequestId, productId: String(door.id) }, recoveryRequestOwner, visitor)) {
        st.finishableRequestId = "";
      }
      if (!finishImages && recoveryRequestId && recoveryRequestOwner
        && this.isCurrentRecoveryRequest(helper, door, { request_id: recoveryRequestId, productId: String(door.id) }, recoveryRequestOwner, visitor)) {
        try { helper.update(String(door.id), recoveryRequestId, recoveryRequestOwner, "RECOVERY_REQUIRED"); } catch (stateError) { /* preserve the recovery error */ }
      }
      if (recoveryRequestId && !this.isCurrentRecoveryRequest(helper, door,
        { request_id: recoveryRequestId, productId: String(door.id) }, recoveryRequestOwner, visitor)) return;
      st.recoveryError = error && error.message === "foreign"
        ? "A design request needs reconciliation before you can continue. Please contact the shop owner for personal assistance."
        : error && error.message === "claim"
        ? "This design could not be linked to your account. Please contact the shop owner for personal assistance."
        : finishImages
          ? "We could not finish the saved images. Your request and current design remain saved. Check the saved request before trying again."
          : "We could not confirm whether the saved request finished. Keep its reference and contact the shop owner for personal assistance before starting another image request.";
      if (this.door === door && this.recoveryStatus) this.recoveryStatus.textContent = st.recoveryError;
      this.refreshGenerationRecovery();
    } finally {
      this.recoveryBusy = false;
      this.refreshGenerationRecovery();
    }
  };

  Take.prototype.loadConceptPreviewImages = async function (data, request, owner, visitor, door) {
    var helper = root.AuriqGenerationRequests;
    var payload = helper && helper.savedPayload(String(door.id), request.request_id, owner);
    var input = data && data.designInput;
    var descriptors = data && (Array.isArray(data.draft_images) ? data.draft_images : data.images);
    var inputProductId = input && input.productId != null ? input.productId : payload && payload.body.productId;
    if (!payload || !input || String(inputProductId) !== String(door.id) || String(payload.body.productId) !== String(door.id) || !door.intake
      || input.formType !== door.intake.formType || !!input.pair !== !!door.intake.pair
      || typeof input.userPrompt !== "string" || !plainStringOptions(input.options)
      || !Array.isArray(descriptors) || descriptors.length !== 1
      || !UUID_RE.test(data.trace_id || "") || !UUID_RE.test(data.revision_id || "") || !UUID_RE.test(data.attempt_id || "")) {
      throw new Error("This concept preview could not be verified. Keep its request reference and ask the owner to help.");
    }
    var savedInput = payload.body;
    if (savedInput.userPrompt !== input.userPrompt || JSON.stringify(savedInput.options) !== JSON.stringify(input.options)
      || savedInput.formType !== input.formType || !!savedInput.pair !== !!input.pair) {
      throw new Error("The returned preview does not match the choices you submitted. It remains unavailable for checkout.");
    }
    var image = descriptors[0];
    if (!image || image.private !== true || image.purchase_ready_reference !== false
      || image.review_state !== "UNREVIEWED_CONCEPT" || !UUID_RE.test(image.asset_id || "")
      || image.trace_id !== data.trace_id || image.revision_id !== data.revision_id || image.attempt_id !== data.attempt_id
      || image.mime_type !== "image/png" || !/^[a-f0-9]{64}$/i.test(image.sha256 || "") || image.url !== undefined) {
      throw new Error("The returned concept preview is missing its private image record.");
    }
    var requestBody = {
      request_id: request.request_id,
      productId: String(door.id),
      trace_id: image.trace_id,
      asset_id: image.asset_id
    };
    if (visitor && visitor.id && visitor.hmac) {
      requestBody.visitor_id = visitor.id;
      requestBody.visitor_hmac = visitor.hmac;
    }
    var response = await root.fetch(this.cfg.appUrl + "/api/design-pilot-asset", {
      method: "POST", credentials: "same-origin", cache: "no-store",
      headers: { "Content-Type": "application/json", Accept: "image/png" },
      body: JSON.stringify(requestBody)
    });
    if (!response.ok || (response.headers.get("Content-Type") || "").split(";")[0].trim() !== "image/png") {
      throw new Error("The private concept image could not be loaded. Its saved request remains available for recovery.");
    }
    var blob = await response.blob();
    if (!blob.size || blob.type && blob.type !== "image/png") throw new Error("The private concept image is empty or has an unexpected format.");
    var objectUrl = root.URL.createObjectURL(blob);
    return [{
      url: objectUrl, fileId: "", private: true, trace_id: image.trace_id, revision_id: image.revision_id,
      attempt_id: image.attempt_id, asset_id: image.asset_id, review_state: image.review_state,
      purchase_ready_reference: false, owner_scope: owner, request_id: request.request_id,
      product_id: String(door.id), designInput: {
        productId: String(door.id), formType: input.formType, pair: !!input.pair,
        options: copy(input.options), userPrompt: input.userPrompt
      }, compiledPrompt: typeof data.compiledPrompt === "string" ? data.compiledPrompt : "",
      designSummary: data.designSummary || null
    }];
  };

  Take.prototype.restoreReadyGallery = async function (door) {
    var st = this.stateFor(door);
    var helper = root.AuriqGenerationRequests;
    if (st.galleryRestoreStarted || !helper || typeof helper.readyRequests !== "function" || !this.cfg.appUrl) return;
    st.galleryRestoreStarted = true;
    var visitor = readVisitor();
    if (!this.cfg.customerId && !visitor) { st.galleryRestoreStarted = false; return; }
    var customerOwner = this.cfg.customerId ? this.currentOwnerScope(visitor) : "";
    var owners = [];
    if (customerOwner) owners.push(customerOwner);
    if (visitor && visitor.id) owners.push("visitor:" + visitor.id);
    if (!owners.length) { st.galleryRestoreStarted = false; return; }
    var requests;
    try { requests = helper.readyRequests(String(door.id), owners); }
    catch (error) { st.galleryRestoreStarted = false; return; }
    var selection = null;
    try { selection = JSON.parse(root.sessionStorage.getItem("auriq.design.gallery-selection.v1:" + String(door.id)) || "null"); }
    catch (error) { selection = null; }
    var restored = [];
    for (var i = 0; i < requests.length; i++) {
      var request = requests[i];
      if ((st.stills || []).some(function (still) { return still.request_id === request.request_id; })) continue;
      try {
        var result = await helper.requestRecovery(this.cfg.appUrl, request, request.owner, customerOwner, visitor);
        if (!result.response.ok || !result.data || result.data.success !== true
          || result.data.state !== "CONCEPT_PREVIEW" || result.data.recovery_state !== "SUCCEEDED") continue;
        var settled = await helper.settleRecovery(this.cfg.appUrl, String(door.id), request, request.owner, customerOwner, visitor, result);
        var images = await this.loadConceptPreviewImages(result.data, request, settled.owner, visitor, door);
        images.forEach(function (image) { image.owner_scope = settled.owner; });
        st.pilotObjectUrls = (st.pilotObjectUrls || []).concat(images.map(function (image) { return image.url; }));
        restored = restored.concat(images);
      } catch (error) {
        // Keep each request reference and any images already recovered; later attempts remain available.
      }
    }
    if (restored.length) st.stills = (st.stills || []).concat(restored);
    var selected = selection && restored.find(function (still) {
      return still.request_id === selection.request_id && still.asset_id === selection.asset_id;
    });
    if (!selected && !st.selected) {
      for (var j = st.stills.length - 1; j >= 0; j--) {
        if (st.stills[j].selection_persisted !== false) { selected = st.stills[j]; break; }
      }
    }
    if (selected) {
      st.selected = selected;
      st.designInput = selected.designInput;
      st.designSummary = selected.designSummary;
      st.compiledPrompt = selected.compiledPrompt;
      if (!selection) {
        try { root.sessionStorage.setItem("auriq.design.gallery-selection.v1:" + String(door.id), JSON.stringify({ request_id: selected.request_id, asset_id: selected.asset_id })); }
        catch (error) { /* selected image is still private and visible in this page */ }
      }
    }
    if (this.door === door) {
      this.paintStill();
      this.revealCanvas();
    }
  };

  Take.prototype.onTake = function (edit) {
    var self = this;
    if (this.busy || this.recoveryBusy || !this.door || this.door.kind !== "door") return;
    var door = this.door;
    var st = this.stateFor(door);
    var heldPilotImage = PILOT_IMAGE_STATES.indexOf(st.pilotReviewState) !== -1 && st.pilotDraftLoaded && st.pilotDraftEditable;
    if (LEGACY_PILOT_STATES.indexOf(st.pilotReviewState) !== -1
      && PILOT_INTENT_STATES.concat(PILOT_IMAGE_STATES).indexOf(st.pilotReviewState) === -1) {
      this.setStatus("This earlier owner-review record is read-only. It cannot be replayed, edited or added to checkout.");
      return;
    }
    if (PILOT_IMAGE_STATES.indexOf(st.pilotReviewState) !== -1 && !edit) {
      this.setStatus(heldPilotImage
        ? "This image remains held from purchase. Use the selected-image edit controls or check its review status; a new image request is unavailable here."
        : "This private draft is awaiting its image review. No new request or edit is available until that review is recorded.");
      return;
    }
    if (PILOT_IMAGE_STATES.indexOf(st.pilotReviewState) !== -1 && edit && !heldPilotImage) {
      this.setStatus("This private draft is available to review, but editing is unavailable until its image review is recorded.");
      return;
    }
    if (st.savedDesignConflict) {
      this.setStatus("This saved design needs a choice update before a new image can be made. Adjust the available choices or contact the shop owner for personal assistance.");
      return;
    }
    if (edit && !st.selectedReady) {
      this.setStatus("Wait until the selected image is displayed before editing it.");
      return;
    }
    if (edit && (!st.selected || !edit.source || edit.source.asset_id !== st.selected.asset_id ||
        edit.source.trace_id !== st.selected.trace_id || edit.source.attempt_id !== st.selected.attempt_id)) {
      throw new Error("The selected design changed. Choose its edit region again.");
    }
    var editMode = null;
    if (edit) {
      editMode = edit.mode === "whole" ? "whole" : edit.mode === "region" || edit.mode == null ? "region" : null;
      if (!editMode || editMode === "region" && (typeof edit.mask !== "string" || !edit.mask) || editMode === "whole" && edit.mask != null) {
        throw new Error("Choose a valid edit approach for the selected design.");
      }
    }
    var motif = edit ? edit.prompt : this.motifText();
    if (edit ? !motifValid(motif) : motif.trim().length > USER_PROMPT_MAX) return;
    if (this.available !== null && this.available <= 0) return;
    if (!this.cfg.appUrl || !door.id) {
      this.setStatus(NOT_SET_UP);
      return;
    }

    st.recoveryError = "";
    if (this.carousel) this.carousel.stopAuto();
    this.setBusy(true);
    this.setStatus("Taking…");
    var hasExistingAttempt = !!(st.lastFailureTraceId || st.traceId || st.attemptId);

    var sourceNeedsClaim = !!(edit && this.cfg.customerId && st.selected && typeof st.selected.owner_scope === "string"
      && st.selected.owner_scope.indexOf("visitor:") === 0);
    var visitorReady = ensureVisitorForExplicitAction();
    var requestId = "";
    var sentToServer = false;
    var responseReceived = false;
    var responseDispatchState = "";
    var responseMayHaveAttempt = false;
    var requestOwnerScope = "";
    var requestVisitor = null;
    function sendGeneration(visitor) {
      var body = {
        productId: String(door.id),
        formType: door.intake.formType,
        pair: !!door.intake.pair,
        options: copy(st.options),
        userPrompt: motif,
        imageCount: 1
      };
      if (visitor) {
        body.visitor_id = visitor.id;
        body.visitor_hmac = visitor.hmac;
      }
      if (edit && st.selected && st.selected.trace_id && st.selected.attempt_id) {
        body.trace_id = st.selected.trace_id;
        body.parent_attempt_id = st.selected.attempt_id;
      } else if (!edit && hasLineage(st.lastFailureTraceId, st.lastFailureAttemptId)) {
        body.trace_id = st.lastFailureTraceId;
        body.parent_attempt_id = st.lastFailureAttemptId;
      } else if (st.selected && st.selected.trace_id && st.selected.attempt_id) {
        body.trace_id = st.selected.trace_id;
        body.parent_attempt_id = st.selected.attempt_id;
      } else if (st.lastFailureTraceId) {
        body.trace_id = st.lastFailureTraceId;
      } else if (st.helpTraceId && !hasExistingAttempt) {
        body.trace_id = st.helpTraceId;
      }
      if (!edit && self.helpProposalStatus === "ACCEPTED" && st.acceptedHelpReference) {
        body.trace_id = st.acceptedHelpTraceId;
        if (st.acceptedHelpParentAttemptId) body.parent_attempt_id = st.acceptedHelpParentAttemptId;
        body.accepted_help_reference = st.acceptedHelpReference;
      }
      if (edit) {
        if (!st.selected || edit.source.asset_id !== st.selected.asset_id || edit.source.trace_id !== st.selected.trace_id ||
            edit.source.attempt_id !== st.selected.attempt_id) {
          throw new Error("The selected design changed. Choose its edit region again.");
        }
        body.edit_source_asset_id = edit.source.asset_id;
        if (editMode === "whole") body.edit_mode = "whole";
        else body.edit_mask_png_base64 = edit.mask;
      }
      requestOwnerScope = self.currentOwnerScope(visitor);
      var requestStore = root.AuriqGenerationRequests;
      if (!requestStore) throw new Error("Saved request recovery is unavailable. Please contact the shop owner for personal assistance before continuing.");
      var sessionContext = root.AuriqDesignHelp && root.AuriqDesignHelp.requestContext && root.AuriqDesignHelp.requestContext();
      if (sessionContext) Object.assign(body, sessionContext);
      var admission = requestStore.begin(String(door.id), body, requestOwnerScope);
      if (admission.blocked) {
        self.refreshGenerationRecovery();
        if (admission.blocked_by_other_owner) {
          throw new Error("A design request needs reconciliation before you can continue. Please contact the shop owner for personal assistance.");
        }
        throw new Error("A previous image request needs to be checked first. Use Check saved request before generating another image.");
      }
      requestId = admission.request_id;
      body.request_id = requestId;
      track("customizer_prompt_submit", withCtx(door, { prompt_length: motif.length }));
      sentToServer = true;
      return root.fetch(self.cfg.appUrl + "/api/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      }).then(function (res) { return requestStore.readGenerationResponse(res, requestId); });
    }
    return visitorReady.then(function (visitor) {
      requestVisitor = visitor;
      if (!visitor || !visitor.id || !visitor.hmac) {
        throw new Error("Your session is still starting. Try again in a moment.");
      }
      if (sourceNeedsClaim) {
        if (!visitor || !st.selected || st.selected.owner_scope !== "visitor:" + visitor.id) {
          throw new Error("This guest image must be reopened in its original browser before it can be edited from your account.");
        }
        return self.claimSelectedStill(door, st.selected).then(function () { return sendGeneration(visitor); });
      }
      return sendGeneration(visitor);
    }).then(function (res) {
      return { res: res.response, data: res.data };
    }).then(function (r) {
      var data = r.data;
      responseReceived = true;
      if (data.state === data.dispatch_state && PILOT_INTENT_STATES.indexOf(data.state) !== -1) {
        var intentRequest = root.AuriqGenerationRequests.recoveryRequest(String(door.id), requestId, requestOwnerScope);
        if (!intentRequest) throw new Error("The saved request could not be retained. Keep its reference and contact the shop owner for personal assistance.");
        self.showPilotReviewState(Object.assign({}, data, {
          recovery_state: "MANUAL_REVIEW", pilot_review_state: data.state, draft_images: Array.isArray(data.draft_images) ? data.draft_images : []
        }), intentRequest, requestOwnerScope, null, st, door);
        if (self.door === door) self.refreshGenerationRecovery();
        track("customizer_generate_response", withCtx(door, { success: false, state: data.state }));
        return;
      }
      if (data.state === data.dispatch_state && PILOT_IMAGE_STATES.indexOf(data.state) !== -1) {
        var imageRequest = root.AuriqGenerationRequests.recoveryRequest(String(door.id), requestId, requestOwnerScope);
        if (!imageRequest) throw new Error("The saved image request could not be retained. Check its reference before continuing.");
        root.AuriqGenerationRequests.update(String(door.id), requestId, requestOwnerScope, data.state, {
          trace_id: data.trace_id, attempt_id: data.attempt_id
        });
        return self.loadPilotDraftImages(Object.assign({}, data, {
          recovery_state: "MANUAL_REVIEW", pilot_review_state: data.state
        }), imageRequest, requestOwnerScope, requestVisitor, st, door).then(function () {
          if (self.door === door) self.refreshGenerationRecovery();
          track("customizer_generate_response", withCtx(door, { success: false, state: data.state }));
        });
      }
      responseDispatchState = typeof data.dispatch_state === "string" ? data.dispatch_state : "";
      responseMayHaveAttempt = responseDispatchState !== "NOT_DISPATCHED" || data.prior_attempt_may_exist === true;
      var responseHasLineage = hasLineage(data.trace_id, data.attempt_id);
      if (responseHasLineage) {
        st.lastFailureTraceId = data.trace_id;
        st.lastFailureAttemptId = data.attempt_id;
      } else if (UUID_RE.test(data.trace_id || "")) {
        st.lastFailureTraceId = data.trace_id;
        st.lastFailureAttemptId = "";
      }
      if (responseDispatchState === "OUTCOME_UNKNOWN" || data.state === "OUTCOME_UNKNOWN"
        || data.state === "DISPATCH_AUTHORIZATION_UNKNOWN"
        || (responseMayHaveAttempt && !responseHasLineage)) st.unresolvedAttempt = true;
      if (data.reconciliation_pending === true) {
        self.setQuota(undefined, undefined, undefined, 0,
          Math.max(typeof data.pending === "number" ? data.pending : 0, self.pending, 1), false);
        st.handoffReason = "QUOTA";
      } else if (data.traffic_throttled === true) {
        self.setQuota(data.rateLimitRemaining, data.rateLimitCap, data.resetsAt, 0,
          typeof data.pending === "number" ? data.pending : self.pending, true);
        st.handoffReason = "QUOTA";
      } else if (typeof data.rateLimitRemaining === "number") {
        self.setQuota(data.rateLimitRemaining, data.rateLimitCap, data.resetsAt, data.available, data.pending, false);
        if (data.rateLimitExceeded === true) st.handoffReason = "QUOTA";
      }
      if (data.dispatch_state === "NOT_DISPATCHED" && data.prior_attempt_may_exist !== true) {
        root.AuriqGenerationRequests.clear(String(door.id), requestId, requestOwnerScope);
      } else if (responseMayHaveAttempt) {
        root.AuriqGenerationRequests.update(String(door.id), requestId, requestOwnerScope, "RECOVERY_REQUIRED",
          responseHasLineage ? { trace_id: data.trace_id, attempt_id: data.attempt_id } : undefined);
      }
      if (self.door === door) self.refreshGenerationRecovery();
      if (r.res.status === 400 && /not configured/i.test(String(data.error || ""))) {
        throw new Error(NOT_SET_UP);
      }
      if (!r.res.ok || !data.success) {
        var reference = data.trace_id || (st.selected && st.selected.trace_id);
        throw new Error((data.error || "The take did not come back. Try again.") +
          (UUID_RE.test(reference || "") ? " Support reference: " + reference + "." : ""));
      }
      if (data.state === "CONCEPT_PREVIEW") {
        var previousSelection = st.selected;
        root.AuriqGenerationRequests.update(String(door.id), requestId, requestOwnerScope, "READY_TO_CONTINUE", {
          trace_id: data.trace_id, attempt_id: data.attempt_id
        });
        return self.loadConceptPreviewImages(data, { request_id: requestId }, requestOwnerScope, requestVisitor, door).then(function (images) {
          if (self.door !== door) {
            root.URL.revokeObjectURL(images[0].url);
            return;
          }
          st.pilotObjectUrls = (st.pilotObjectUrls || []).concat([images[0].url]);
          st.stills = (st.stills || []).concat(images);
          if (self.savedDesignHistory) {
            self.savedDesignHistory = null;
            st.savedDesignChanged = false;
            st.savedDesignConflict = false;
          }
          st.traceId = data.trace_id;
          st.revisionId = data.revision_id;
          st.attemptId = data.attempt_id;
          st.lastFailureTraceId = "";
          st.lastFailureAttemptId = "";
          st.unresolvedAttempt = false;
          st.recoveryPartial = false;
          st.recoveryPending = false;
          st.handoffReason = "VOLUNTARY";
          if (!previousSelection) {
            st.selected = images[0];
            st.designInput = images[0].designInput;
            st.designSummary = images[0].designSummary;
            st.compiledPrompt = images[0].compiledPrompt;
            try {
              images[0].selection_persisted = !!root.AuriqGenerationRequests.recordSelection(
                String(door.id), requestId, requestOwnerScope, images[0].asset_id,
                { trace_id: images[0].trace_id, attempt_id: images[0].attempt_id });
            } catch (storageError) { images[0].selection_persisted = false; }
          }
          self.setStatus(previousSelection
            ? "A new concept preview is ready. Your selected reference is unchanged; choose the new preview if you want to continue with it."
            : "Concept preview ready. Final materials, dimensions and construction are confirmed with the owner.");
          if (self.door === door) {
            self.paintStill();
            self.revealCanvas();
            if (self.take) self.take.textContent = self.take.getAttribute("data-label-again") || "Try another design";
            self.refreshGenerationRecovery();
          }
          track("customizer_generate_response", withCtx(door, { success: true, image_count: images.length, state: "CONCEPT_PREVIEW" }));
        });
      }
      var images = Array.isArray(data.images) && data.images.length
        ? data.images
        : (data.imageUrl ? [{ url: data.imageUrl, fileId: data.fileId }] : []);
      if (!images.length) throw new Error("The take did not come back. Try again.");
      var oldSelection = st.selected;
      var newImages = images.map(function (img) {
        return {
          url: img.url,
          fileId: img.fileId || "",
          trace_id: img.trace_id || data.trace_id || "",
          revision_id: img.revision_id || data.revision_id || "",
          attempt_id: img.attempt_id || data.attempt_id || "",
          asset_id: img.asset_id || "",
          review_state: img.review_state,
          purchase_ready_reference: img.purchase_ready_reference,
          owner_scope: requestOwnerScope,
          request_id: requestId,
          product_id: String(door.id),
          designInput: data.designInput || { productId: String(door.id), formType: door.intake.formType,
            pair: !!door.intake.pair, options: copy(st.options), userPrompt: motif },
          designSummary: data.designSummary || null,
          compiledPrompt: data.compiledPrompt || data.prompt || ""
        };
      });
      st.stills = (st.stills || []).concat(newImages);
      st.compiledPrompt = data.compiledPrompt || data.prompt || "";
      st.designInput = data.designInput || null;
      st.designSummary = data.designSummary || null;
      st.traceId = data.trace_id || st.stills[0].trace_id;
      st.revisionId = data.revision_id || st.stills[0].revision_id;
      st.attemptId = data.attempt_id || st.stills[0].attempt_id;
      var failureCount = Array.isArray(data.candidate_failures) ? data.candidate_failures.length : 0;
      st.lastFailureTraceId = failureCount ? data.trace_id || "" : "";
      st.lastFailureAttemptId = failureCount ? data.attempt_id || "" : "";
      st.unresolvedAttempt = failureCount > 0;
      st.recoveryPartial = failureCount > 0;
      st.recoveryPending = false;
      st.selected = oldSelection || newImages[0];
      if (self.savedDesignHistory) {
        self.savedDesignHistory = null;
        st.savedDesignChanged = false;
        st.savedDesignConflict = false;
      }
      if (failureCount) {
        root.AuriqGenerationRequests.update(String(door.id), requestId, requestOwnerScope, "RECOVERY_REQUIRED",
          UUID_RE.test(data.trace_id || "") && UUID_RE.test(data.attempt_id || "")
            ? { trace_id: data.trace_id, attempt_id: data.attempt_id }
            : undefined);
      } else {
        root.AuriqGenerationRequests.update(String(door.id), requestId, requestOwnerScope, "READY_TO_CONTINUE", {
          trace_id: st.traceId,
          attempt_id: st.attemptId
        });
        try {
          st.selected.selection_persisted = !!root.AuriqGenerationRequests.recordSelection(
            String(door.id), requestId, requestOwnerScope, st.selected.asset_id, {
              trace_id: st.selected.trace_id,
              attempt_id: st.selected.attempt_id
            }
          );
        } catch (storageError) {
          st.selected.selection_persisted = false;
        }
        if (self.helpProposalStatus === "ACCEPTED") self.completeAcceptedDesignHelpProposal(st);
      }
      if (self.door === door) {
        self.setStatus(failureCount
          ? (failureCount === 1 ? "One generated image could not be prepared." : failureCount + " generated images could not be prepared.") + " This preview is incomplete; keep checking the saved request. Saving and checkout are unavailable until recovery completes."
          : oldSelection ? "A new image is available. Your selected reference is unchanged; choose the new image if you want to switch." : "");
        self.paintStill();
        if (self.take) self.take.textContent = failureCount
          ? "Recovery required"
          : (self.take.getAttribute("data-label-again") || "Take again");
        self.revealCanvas();
        self.refreshGenerationRecovery();
        if (!failureCount && st.selected.selection_persisted === false) {
          self.setStatus("We could not retain this image selection for recovery. Please try again.");
        }
      }
      track("customizer_generate_response", withCtx(door, { success: true, image_count: st.stills.length }));
    }).catch(function (err) {
      if (st.handoffReason !== "QUOTA") st.handoffReason = "FAILURE";
      if (sentToServer && !responseReceived) {
        st.unresolvedAttempt = true;
        try {
          root.AuriqGenerationRequests.update(String(door.id), requestId, requestOwnerScope, "RECOVERY_REQUIRED");
        } catch (storageError) { /* keep the durable request if storage cannot be updated */ }
      }
      if (self.door === door) {
        self.setStatus(err && err.message ? err.message : "Something went wrong. Try again.");
        self.refreshGenerationRecovery();
      }
      track("customizer_generate_response", withCtx(door, {
        success: false,
        error_kind: err && err.message ? String(err.message).slice(0, 64) : "unknown"
      }));
      if (edit) throw err;
    }).finally(function () {
      self.setBusy(false);
    });
  };

  Take.prototype.onAddToBag = function () {
    if (this.busy || !this.door || this.door.kind !== "door") return;
    var door = this.door;
    if (!purchasableVariant(door)) {
      this.setStatus("This product option is unavailable. Choose an available option before adding it to your bag.");
      return;
    }
    var st = this.stateFor(door);
    if (st.savedDesignConflict) {
      this.setStatus("This saved design needs a choice update before it can be added to your bag. Contact the shop owner for personal assistance.");
      return;
    }
    if (st.savedDesignChanged) {
      this.setStatus("Take a new image after changing this saved design before adding it to your bag.");
      return;
    }
    if (st.recoveryPartial) {
      this.setStatus("This preview is incomplete. Check the saved request again before adding a design to your bag.");
      return;
    }
    if (st.unresolvedAttempt || st.recoveryPending) {
      this.setStatus("We could not confirm whether the saved request finished. Check its status before ordering.");
      return;
    }
    if (!this.selectedStillMatchesAttempt(st)) {
      this.setStatus("This image belongs to an earlier request. Take a new image before adding it to your bag.");
      return;
    }
    if (!st.selected || !st.selectedReady) return;
    if (st.selected.private === true && st.selected.review_state === "UNREVIEWED_CONCEPT") {
      this.addDesignReferenceToCart(door, st, st.selected, "Concept preview");
      return;
    }
    if (st.selected.purchase_ready_reference !== true) {
      this.setStatus("This image is not ready for purchase. Choose another image or contact the shop owner for personal assistance.");
      return;
    }
    this.addDesignReferenceToCart(door, st, st.selected, "AI Generated");
  };

  Take.prototype.onSave = function () {
    var self = this;
    if (this.busy || !this.door || this.door.kind !== "door" || !this.save) return;
    var door = this.door;
    var st = this.stateFor(door);
    if (st.savedDesignConflict) {
      this.setStatus("This saved design needs a choice update before it can be saved again. Contact the shop owner for personal assistance.");
      return;
    }
    if (st.savedDesignChanged) {
      this.setStatus("Take a new image after changing this saved design before saving it.");
      return;
    }
    if (st.recoveryPartial) {
      this.setStatus("This preview is incomplete. Check the saved request again before saving a design.");
      return;
    }
    if (st.unresolvedAttempt || st.recoveryPending) {
      this.setStatus("We could not confirm whether the saved request finished. Check its status before saving.");
      return;
    }
    if (!this.selectedStillMatchesAttempt(st)) {
      this.setStatus("This image belongs to an earlier request. Take a new image before saving it.");
      return;
    }
    if (!st.selected || !st.selectedReady) return;
    if (typeof st.selected.compiledPrompt !== "string" || !st.selected.compiledPrompt) {
      this.setStatus("The selected image's original request is unavailable. Contact the shop owner for personal assistance before saving it.");
      return;
    }
    if (!this.cfg.customerId) {
      this.showLoginPrompt("Log in to save this design.");
      return;
    }
    this.save.disabled = true;
    this.claimSelectedStill(door, st.selected).then(function () {
      if (self.door !== door) throw new Error("The selected piece changed while we verified its image. Please review it before saving.");
      return root.fetch(self.cfg.appUrl + "/api/favourites", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productId: String(door.id),
          productTitle: door.title,
          imageUrl: st.selected.url,
          fileId: st.selected.fileId,
          prompt: st.selected.compiledPrompt,
          variantId: String(door.variantId || ""),
          traceId: st.selected.trace_id,
          revisionId: st.selected.revision_id,
          attemptId: st.selected.attempt_id,
          assetId: st.selected.asset_id
        })
      });
    }).then(function (res) {
      if (!res) return;
      return res.json().catch(function () { return {}; }).then(function (data) {
        if (!res.ok || !data.success) throw new Error(data.error || "Could not save.");
        self.save.textContent = self.save.getAttribute("data-label-saved") || "Saved";
      });
    }).catch(function (err) {
      self.setStatus(err && err.message ? err.message : "Could not save.");
      self.save.disabled = false;
    });
  };

  Take.navigate = function (url) {
    root.location.assign(url);
  };

  Take.init = function (scope) {
    var nodes = (scope || document).querySelectorAll("[data-auriq-take]");
    var made = [];
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].__auriqTake) { made.push(nodes[i].__auriqTake); continue; }
      var t = new Take(nodes[i]);
      nodes[i].__auriqTake = t;
      made.push(t);
    }
    return made;
  };

  Take.motifValid = motifValid;
  Take.ringPose = ringPose;
  Take.ringSlot = ringSlot;
  Take.PANEL_SWAP_AT_MS = PANEL_SWAP_AT_MS;
  Take.doorFor = doorFor;
  Take.isDoorHandle = isDoorHandle;
  Take.USER_PROMPT_MIN = USER_PROMPT_MIN;
  Take.USER_PROMPT_MAX = USER_PROMPT_MAX;

  root.AuriqTake = Take;

  if (!root.__auriqTakeManualInit) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", function () { Take.init(); });
    } else {
      Take.init();
    }
    document.addEventListener("shopify:section:load", function (e) {
      if (e && e.target) Take.init(e.target);
    });
  }
})(window);
